import {
  ActionStepType,
  BooleanOperator,
  FormulaOperator,
  GeneratedFileType,
  IntegrationType,
  SortDirection,
} from '@doss/prisma/types'
import _ from 'lodash'
import * as yup from 'yup'
import {
  ActionData,
  ActionDataPrimitive,
  actionDataPrimitiveSchema,
  actionDataSchema,
  booleanActionDataSchema,
  numberActionDataSchema,
  stringActionDataSchema,
} from './action-data.js'
import { AI_PROMPT_CONTEXT_TYPES } from './ai-prompt.js'
import {
  ALL_DATASTORE_MUTATION_OPERATIONS,
  DatastoreArrayMutationOperation,
} from './datastore-mutations.js'
import { BARCODE_TYPES } from './generate-barcode.js'
import { JsonValue } from './json.js'
import { Condition, isUnaryCondition, logicExpressionSchema } from './logic.js'
import {
  ACTION_STEP_NOTIFICATION_TYPES,
  LINK_TO_TYPES,
  NOTIFICATION_TARGET_TYPES,
  NOTIFICATION_USER_SELECT_METHODS,
} from './notification.js'
import { lookupCellDataSchema } from './table-cell.js'

export enum ParseFileType {
  CSV = 'CSV',
  SIF = 'SIF',
  PDF = 'PDF',
}

// Modes for writing to an integration
export enum WriteIntegrationMode {
  CANCEL = 'CANCEL',
  CLOSE = 'CLOSE',
  CONFIRM = 'CONFIRM',
  CREATE = 'CREATE',
  DELETE = 'DELETE',
  DISPOSITION = 'DISPOSITION',
  DUPLICATE = 'DUPLICATE',
  EXECUTE = 'EXECUTE',
  FLAG = 'FLAG',
  GRADE = 'GRADE',
  MOVE = 'MOVE',
  PATCH = 'PATCH',
  PROCESS = 'PROCESS',
  RECEIVE = 'RECEIVE',
  REJECT = 'REJECT',
  REORDER = 'REORDER',
  TRANSFORM = 'TRANSFORM',
  UPDATE = 'UPDATE',
  UPSERT = 'UPSERT',
  VOID = 'VOID',
}

// Entity types for writing to an integration
export enum WriteIntegrationEntityType {
  ACH = 'ACH',
  ACCESS_TOKEN = 'ACCESS_TOKEN',
  ACCOUNT = 'ACCOUNT',
  ACCOUNTING_SYNC = 'ACCOUNTING_SYNC',
  ACKNOWLEDGEMENT = 'ACKNOWLEDGEMENT',
  ADVANCED_SHIPPING_NOTICE = 'ADVANCED_SHIPPING_NOTICE',
  ADVANCED_SHIPPING_NOTICE_ITEMS = 'ADVANCED_SHIPPING_NOTICE_ITEMS',
  ADVANCED_SHIPPING_NOTICE_PACKAGING = 'ADVANCED_SHIPPING_NOTICE_PACKAGING',
  ADVANCED_SHIPPING_NOTICE_PACKAGING_ITEM = 'ADVANCED_SHIPPING_NOTICE_PACKAGING_ITEM',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  ASN = 'ASN', // Advanced Shipping Notice
  ASSET_COST = 'ASSET_COST',
  ASSEMBLY_BUILD = 'ASSEMBLY_BUILD',
  ASSEMBLY_ITEM = 'ASSEMBLY_ITEM',
  ASSEMBLY_UNBUILD = 'ASSEMBLY_UNBUILD',
  ACCOUNTING_FIELD = 'ACCOUNTING_FIELD',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  BANK_FEED = 'BANK_FEED',
  BANK_RECONCILIATION_REPORT = 'BANK_RECONCILIATION_REPORT',
  BANK_TRANSACTION = 'BANK_TRANSACTION',
  BENEFIT_POLICY = 'BENEFIT_POLICY',
  BILL = 'BILL',
  BILL_AMORTIZATION = 'BILL_AMORTIZATION',
  BILL_AMORTIZATION_GLOBAL = 'BILL_AMORTIZATION_GLOBAL',
  BILL_APPROVAL = 'BILL_APPROVAL',
  BILLS = 'BILLS',
  BILL_DOCUMENT = 'BILL_DOCUMENT',
  BILL_PAYMENT = 'BILL_PAYMENT',
  COMPANY_LOCATION = 'COMPANY_LOCATION',
  COMPANY_LOCATION_ASSIGN_ROLES = 'COMPANY_LOCATION_ASSIGN_ROLES',
  COMPANY_LOCATION_TAX = 'COMPANY_LOCATION_TAX',
  CASH_SALE = 'CASH_SALE',
  CONTRACT = 'CONTRACT',
  CONTRACT_AMENDMENT = 'CONTRACT_AMENDMENT',
  CONTRACT_END = 'CONTRACT_END',
  COST_ALLOCATION = 'COST_ALLOCATION',
  COST_ALLOCATIONS = 'COST_ALLOCATIONS',
  CHART_ACCOUNT_SETTINGS = 'CHART_ACCOUNT_SETTINGS',
  CHART_ENTITY = 'CHART_ENTITY',
  CHART_TRANSACTION = 'CHART_TRANSACTION',
  CHARGE = 'CHARGE',
  BIN = 'BIN',
  BOM = 'BOM',
  BOM_REVISION = 'BOM_REVISION',
  BUDGET = 'BUDGET',
  BUDGET_ACCOUNT = 'BUDGET_ACCOUNT',
  BULK_INVOICE = 'BULK_INVOICE',
  BULK_INVOICE_PAYMENT = 'BULK_INVOICE_PAYMENT',
  BUNDLE = 'BUNDLE',
  BUSINESS_PLANNING_BUDGET = 'BUSINESS_PLANNING_BUDGET',
  BUY_SHIPMENT = 'BUY_SHIPMENT',
  BUY_ORDER = 'BUY_ORDER',
  BUYER = 'BUYER',
  BUYER_CONTACT = 'BUYER_CONTACT',
  CHART_TRANSACTIONS = 'CHART_TRANSACTIONS',
  CHECK_PAYEE_NAME = 'CHECK_PAYEE_NAME',
  COMMITMENT = 'COMMITMENT',
  CREDIT_LINE = 'CREDIT_LINE',
  CREDIT_MEMO = 'CREDIT_MEMO',
  CREDIT_MEMO_SEND = 'CREDIT_MEMO_SEND',
  COMPANY = 'COMPANY',
  COMPANY_ASSIGN_CUSTOMER_CONTACT = 'COMPANY_ASSIGN_CUSTOMER_CONTACT',
  COMPANY_CONTACT = 'COMPANY_CONTACT',
  COMPANY_INFO = 'COMPANY_INFO',
  COMPANY_MAIN_CONTACT = 'COMPANY_MAIN_CONTACT',
  CONTACT = 'CONTACT',
  CONTRACT_BUNDLE = 'CONTRACT_BUNDLE',
  CONTRACT_BUNDLE_ALLOCATIONS = 'CONTRACT_BUNDLE_ALLOCATIONS',
  CONTRACT_ITEM_USAGE_RECORD = 'CONTRACT_ITEM_USAGE_RECORD',
  CONTRACT_MILESTONE = 'CONTRACT_MILESTONE',
  CONTRACT_SUBSCRIPTION = 'CONTRACT_SUBSCRIPTION',
  CONTRACT_USAGE_REVENUE = 'CONTRACT_USAGE_REVENUE',
  CUSTOM_DIMENSION_GROUP = 'CUSTOM_DIMENSION_GROUP',
  CUSTOM_DIMENSION = 'CUSTOM_DIMENSION',
  CUSTOM_FIELD = 'CUSTOM_FIELD',
  CUSTOM_FIELD_DEFINITION = 'CUSTOM_FIELD_DEFINITION',
  CUSTOMER = 'CUSTOMER',
  DEPARTMENT = 'DEPARTMENT',
  CUSTOMER_CURRENCY = 'CUSTOMER_CURRENCY',
  DEAL = 'DEAL',
  DEBIT_MEMO = 'DEBIT_MEMO',
  DEPOSIT = 'DEPOSIT',
  DISPOSITION = 'DISPOSITION',
  DROPSHIP_ORDER = 'DROPSHIP_ORDER',
  EMAIL = 'EMAIL',
  EMPLOYEE = 'EMPLOYEE',
  EMPLOYEE_BENEFIT = 'EMPLOYEE_BENEFIT',
  EMPLOYEE_EMPLOYMENT_DETAIL = 'EMPLOYEE_EMPLOYMENT_DETAIL',
  EMPLOYEE_MISC_DEDUCTION = 'EMPLOYEE_MISC_DEDUCTION',
  EMPLOYEE_PENSION = 'EMPLOYEE_PENSION',
  ENTITY_INVOICE_EMAIL_ATTACHMENT = 'ENTITY_INVOICE_EMAIL_ATTACHMENT',
  ESTIMATE = 'ESTIMATE',
  ACCEPT_DELIVERY = 'ACCEPT_DELIVERY',
  FAIL_DELIVERY = 'FAIL_DELIVERY',
  FIELD_DEFINITION = 'FIELD_DEFINITION',
  FIELD_VALUE = 'FIELD_VALUE',
  FILE = 'FILE',
  FORMAT_CONVERSION = 'FORMAT_CONVERSION',
  FRAUD_REPORT = 'FRAUD_REPORT',
  FIXED_ASSET = 'FIXED_ASSET',
  FIXED_ASSET_CLASS = 'FIXED_ASSET_CLASS',
  FIXED_ASSET_AUTOMATION = 'FIXED_ASSET_AUTOMATION',
  FIXED_ASSET_AUTOMATION_MATCH = 'FIXED_ASSET_AUTOMATION_MATCH',
  FULFILLMENT = 'FULFILLMENT',
  FULFILLMENT_ORDER = 'FULFILLMENT_ORDER',
  GL_ACCOUNT = 'GL_ACCOUNT',
  GRADE_ITEM = 'GRADE_ITEM',
  HOLD_ORDER = 'HOLD_ORDER',
  INBOUND_ASN = 'INBOUND_ASN',
  INBOUND_PLAN = 'INBOUND_PLAN',
  INTERCOMPANY_JOURNAL_ENTRY = 'INTERCOMPANY_JOURNAL_ENTRY',
  INVENTORY = 'INVENTORY',
  INVENTORY_ADJUSTMENT = 'INVENTORY_ADJUSTMENT',
  INVENTORY_COUNT = 'INVENTORY_COUNT',
  INVENTORY_ITEM = 'INVENTORY_ITEM',
  INVENTORY_NUMBER = 'INVENTORY_NUMBER',
  INVENTORY_ORDER = 'INVENTORY_ORDER',
  INVENTORY_ORDER_INTERNAL = 'INVENTORY_ORDER_INTERNAL',
  INVENTORY_ORDER_ITEM_STATUS = 'INVENTORY_ORDER_ITEM_STATUS',
  INVENTORY_ORDER_ITEM_QUANTITY = 'INVENTORY_ORDER_ITEM_QUANTITY',
  INVENTORY_STATUS = 'INVENTORY_STATUS',
  INCOMING_TRANSFER = 'INCOMING_TRANSFER',
  INVENTORY_TRANSFER = 'INVENTORY_TRANSFER',
  INVITATION = 'INVITATION',
  INVOICE = 'INVOICE',
  INVOICE_SENT_STATUS = 'INVOICE_SENT_STATUS',
  INVOICE_TAXES = 'INVOICE_TAXES',
  CREDIT_MEMO_TAXES = 'CREDIT_MEMO_TAXES',
  CREDIT_MEMO_AS_USED = 'CREDIT_MEMO_AS_USED',
  INVOICE_PAYMENT = 'INVOICE_PAYMENT',
  INVOICE_PAYMENT_CALCULATION = 'INVOICE_PAYMENT_CALCULATION',
  MANUAL_JOURNAL = 'MANUAL_JOURNAL',
  ITEM = 'ITEM',
  ITEM_RECEIPT = 'ITEM_RECEIPT',
  ITEMS_FOR_EXISTING_ASN = 'ITEMS_FOR_EXISTING_ASN',
  JOURNAL_ENTRY = 'JOURNAL_ENTRY',
  LABEL = 'LABEL',
  LINE_ITEM = 'LINE_ITEM',
  LISTING = 'LISTING',
  LISTINGS_ITEMS = 'LISTINGS_ITEMS',
  LOCATION = 'LOCATION',
  LOT_NUMBERED_INVENTORY_ITEM = 'LOT_NUMBERED_INVENTORY_ITEM',
  NON_INVENTORY_PURCHASE_ITEM = 'NON_INVENTORY_PURCHASE_ITEM',
  NON_INVENTORY_RESALE_ITEM = 'NON_INVENTORY_RESALE_ITEM',
  NON_INVENTORY_SALE_ITEM = 'NON_INVENTORY_SALE_ITEM',
  ORDER = 'ORDER',
  ORDER_V7 = 'ORDER_V7',
  ORDER_LINES = 'ORDER_LINES',
  ORDER_ACKNOWLEDGEMENT = 'ORDER_ACKNOWLEDGEMENT',
  SERIALIZED_INVENTORY_ITEM = 'SERIALIZED_INVENTORY_ITEM',
  OUTBOUND_ASN = 'OUTBOUND_ASN',
  PACKAGE_SHIPPING = 'PACKAGE_SHIPPING',
  PACKING_INFORMATION = 'PACKING_INFORMATION',
  PACKING_OPTIONS = 'PACKING_OPTIONS',
  PAYMENT = 'PAYMENT',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  VALIDATE_PAYMENT_METHOD = 'VALIDATE_PAYMENT_METHOD',
  PAYMENT_LINK = 'PAYMENT_LINK',
  PLACEMENT_OPTIONS = 'PLACEMENT_OPTIONS',
  PO_LINES = 'PO_LINES',
  PAYABLE = 'PAYABLE',
  PAYMENT_REQUEST = 'PAYMENT_REQUEST',
  PENSION_POLICY = 'PENSION_POLICY',
  PRICE = 'PRICE',
  PRICE_LIST = 'PRICE_LIST',
  PRICE_LIST_FIXED_PRICES = 'PRICE_LIST_FIXED_PRICES',
  PRODUCT = 'PRODUCT',
  PRODUCT_BUNDLE = 'PRODUCT_BUNDLE',
  PRODUCTS = 'PRODUCTS',
  PRODUCT_VARIANT = 'PRODUCT_VARIANT',
  PROJECT = 'PROJECT',
  PURCHASE = 'PURCHASE',
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  MATERIAL = 'MATERIAL',
  MISC_DEDUCTION_POLICY = 'MISC_DEDUCTION_POLICY',
  STYLE = 'STYLE',
  DIRECTORY = 'DIRECTORY',
  DRAFT_ORDER = 'DRAFT_ORDER',
  QUOTE = 'QUOTE',
  RATE_QUOTE = 'RATE_QUOTE',
  RECEIPT = 'RECEIPT',
  SALES_ORDER = 'SALES_ORDER',
  SALES_RECEIPT = 'SALES_RECEIPT',
  REIMBURSEMENT = 'REIMBURSEMENT',
  RELEASE_ASN = 'RELEASE_ASN',
  RECEIVING = 'RECEIVING',
  RECORD = 'RECORD',
  REFUND_ORDER = 'REFUND_ORDER',
  REFUND_RECEIPT = 'REFUND_RECEIPT',
  REFUND_SHIPMENT = 'REFUND_SHIPMENT',
  RELEASE_ORDER = 'RELEASE_ORDER',
  REPORT_SCHEDULE = 'REPORT_SCHEDULE',
  RETURN = 'RETURN',
  RETURN_NOTE = 'RETURN_NOTE',
  REVENUE_TRANSACTION = 'REVENUE_TRANSACTION',
  SHIP_ORDER_LINES = 'SHIP_ORDER_LINES',
  SHIP_PACKAGE = 'SHIP_PACKAGE',
  SHIPMENT = 'SHIPMENT',
  SHIPMENT_CONFIRMATION = 'SHIPMENT_CONFIRMATION',
  SHIPMENT_STATUS = 'SHIPMENT_STATUS',
  SHIPMENT_ITEMS = 'SHIPMENT_ITEMS',
  CATALOG = 'CATALOG',
  SHIPPING_LABEL = 'SHIPPING_LABEL',
  SPLIT_ORDERS = 'SPLIT_ORDERS',
  SKU = 'SKU',
  SUBSCRIPTION = 'SUBSCRIPTION',
  SUBSIDIARY = 'SUBSIDIARY',
  TAX_AGENCY = 'TAX_AGENCY',
  TAX_CALCULATION = 'TAX_CALCULATION',
  TENDER = 'TENDER',
  TRACKING = 'TRACKING',
  TRACKING_NUMBER = 'TRACKING_NUMBER',
  TRANSACTION = 'TRANSACTION',
  TRANSACTION_MATCH = 'TRANSACTION_MATCH',
  TRANSACTION_MATCH_STATUS = 'TRANSACTION_MATCH_STATUS',
  TRANSFER = 'TRANSFER',
  TRANSFER_ORDER = 'TRANSFER_ORDER',
  TRANSPORTATION_OPTIONS = 'TRANSPORTATION_OPTIONS',
  UNAWARD = 'UNAWARD',
  VENDOR = 'VENDOR',
  VENDOR_CREDIT = 'VENDOR_CREDIT',
  VENDOR_CUSTOM_FIELD_1 = 'VENDOR_CUSTOM_FIELD_1',
  VENDOR_BANK = 'VENDOR_BANK',
  WORK_ORDER = 'WORK_ORDER',
  WORK_ORDER_CLOSE = 'WORK_ORDER_CLOSE',
  WORK_ORDER_COMPLETION = 'WORK_ORDER_COMPLETION',
  WORK_ORDER_ISSUE = 'WORK_ORDER_ISSUE',
  BILLING_ACCOUNT = 'BILLING_ACCOUNT',
  VENDOR_CATEGORY = 'VENDOR_CATEGORY',
  VENDOR_PAYMENT = 'VENDOR_PAYMENT',
  VENDOR_BILL = 'VENDOR_BILL',
  CUSTOMER_CATEGORY = 'CUSTOMER_CATEGORY',
  CUSTOMER_DEPOSIT = 'CUSTOMER_DEPOSIT',
  CUSTOMER_PAYMENT = 'CUSTOMER_PAYMENT',
  CUSTOMER_REFUND = 'CUSTOMER_REFUND',
  CUSTOMER_STATUS = 'CUSTOMER_STATUS',
  DESCRIPTION_ITEM = 'DESCRIPTION_ITEM',
  DISCOUNT_ITEM = 'DISCOUNT_ITEM',
  DOWNLOAD_ITEM = 'DOWNLOAD_ITEM',
  GIFT_CERTIFICATE_ITEM = 'GIFT_CERTIFICATE_ITEM',
  ITEM_ACCOUNT_MAPPING = 'ITEM_ACCOUNT_MAPPING',
  ITEM_FULFILLMENT = 'ITEM_FULFILLMENT',
  ITEM_GROUP = 'ITEM_GROUP',
  ITEM_REVISION = 'ITEM_REVISION',
  KIT_ITEM = 'KIT_ITEM',
  MARKUP_ITEM = 'MARKUP_ITEM',
  OTHER_CHARGE_PURCHASE_ITEM = 'OTHER_CHARGE_PURCHASE_ITEM',
  OTHER_CHARGE_RESALE_ITEM = 'OTHER_CHARGE_RESALE_ITEM',
  OTHER_CHARGE_SALE_ITEM = 'OTHER_CHARGE_SALE_ITEM',
  PAYMENT_ITEM = 'PAYMENT_ITEM',
  PAYROLL_ITEM = 'PAYROLL_ITEM',
  SALES_TAX_ITEM = 'SALES_TAX_ITEM',
  SERVICE_PURCHASE_ITEM = 'SERVICE_PURCHASE_ITEM',
  SERVICE_RESALE_ITEM = 'SERVICE_RESALE_ITEM',
  SERVICE_SALE_ITEM = 'SERVICE_SALE_ITEM',
  SHIP_ITEM = 'SHIP_ITEM',
  SUBTOTAL_ITEM = 'SUBTOTAL_ITEM',
  CLASSIFICATION = 'CLASSIFICATION',
}

// Entity types for reading from an integration
export enum ReadIntegrationEntityType {
  ACCOUNT = 'ACCOUNT',
  ACCOUNTS = 'ACCOUNTS',
  ACKNOWLEDGEMENT = 'ACKNOWLEDGEMENT',
  ASN = 'ASN',
  ASN_ITEMS = 'ASN_ITEMS',
  ASN_LIST = 'ASN_LIST',
  ASN_PACKAGING = 'ASN_PACKAGING',
  ASN_RECEIVING_DISCREPANCY_REPORT = 'ASN_RECEIVING_DISCREPANCY_REPORT',
  ASN_STATUS = 'ASN_STATUS',
  ASSEMBLY_BUILD = 'ASSEMBLY_BUILD',
  ASSEMBLY_BUILDS = 'ASSEMBLY_BUILDS',
  ASSEMBLY_ITEM = 'ASSEMBLY_ITEM',
  ASSEMBLY_ITEMS = 'ASSEMBLY_ITEMS',
  ASSEMBLY_UNBUILD = 'ASSEMBLY_UNBUILD',
  ASSEMBLY_UNBUILDS = 'ASSEMBLY_UNBUILDS',
  ASSET_COSTS = 'ASSET_COSTS',
  APPLICATION_ADVICE = 'APPLICATION_ADVICE',
  ATTACHMENT = 'ATTACHMENT',
  AVAILABILITY = 'AVAILABILITY',
  BALANCE_SHEET = 'BALANCE_SHEET',
  BALANCE_SHEET_REPORT = 'BALANCE_SHEET_REPORT',
  B2B_COMPANIES = 'B2B_COMPANIES',
  B2B_COMPANY = 'B2B_COMPANY',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  BANK_ACCOUNTS = 'BANK_ACCOUNTS',
  BANK_RECONCILIATION_REPORT = 'BANK_RECONCILIATION_REPORT',
  BANK_RECONCILIATION_REPORTS = 'BANK_RECONCILIATION_REPORTS',
  BANK_TRANSACTION = 'BANK_TRANSACTION',
  BANK_TRANSACTIONS = 'BANK_TRANSACTIONS',
  BILL = 'BILL',
  BILL_AMORTIZATION = 'BILL_AMORTIZATION',
  BILL_AMORTIZATION_GLOBAL = 'BILL_AMORTIZATION_GLOBAL',
  BILL_DOCUMENTS = 'BILL_DOCUMENTS',
  BILL_EMPTY_TRANSACTION_DEFAULTS = 'BILL_EMPTY_TRANSACTION_DEFAULTS',
  BILLS = 'BILLS',
  BILLS_BULK_SEARCH = 'BILLS_BULK_SEARCH',
  RECONCILIATION_PARSE_STATEMENT_PAGE = 'RECONCILIATION_PARSE_STATEMENT_PAGE',
  BILL_PAYMENT = 'BILL_PAYMENT',
  BILL_PAYMENTS = 'BILL_PAYMENTS',
  BULK_INVOICE_SEARCH = 'BULK_INVOICE_SEARCH',
  CASH_BASIS_INCOME_STATEMENT = 'CASH_BASIS_INCOME_STATEMENT',
  CASH_BASIS_OPERATING_STATEMENT = 'CASH_BASIS_OPERATING_STATEMENT',
  CASH_FLOW = 'CASH_FLOW',
  CASH_SALE = 'CASH_SALE',
  CASH_SALES = 'CASH_SALES',
  CONTRACT = 'CONTRACT',
  CONTRACTS = 'CONTRACTS',
  CONTRACT_BULK_SEARCH = 'CONTRACT_BULK_SEARCH',
  CONTRACT_BUNDLE = 'CONTRACT_BUNDLE',
  CONTRACT_BUNDLES = 'CONTRACT_BUNDLES',
  CONTRACT_MILESTONE = 'CONTRACT_MILESTONE',
  CONTRACT_MILESTONES = 'CONTRACT_MILESTONES',
  CONTRACT_BUNDLE_ALLOCATIONS = 'CONTRACT_BUNDLE_ALLOCATIONS',
  CONTRACT_SUBSCRIPTION = 'CONTRACT_SUBSCRIPTION',
  CONTRACT_SUBSCRIPTIONS = 'CONTRACT_SUBSCRIPTIONS',
  CONTRACT_USAGE_REVENUE = 'CONTRACT_USAGE_REVENUE',
  CONTRACT_USAGE_REVENUES = 'CONTRACT_USAGE_REVENUES',
  COMMERCIAL_INVOICE = 'COMMERCIAL_INVOICE',
  COMMERCIAL_INVOICES = 'COMMERCIAL_INVOICES',
  CONTAINER = 'CONTAINER',
  CUSTOMS_ENTRY = 'CUSTOMS_ENTRY',
  CUSTOMS_ENTRIES = 'CUSTOMS_ENTRIES',
  CUSTOM_FIELD = 'CUSTOM_FIELD',
  CHARGE = 'CHARGE',
  CHARGES = 'CHARGES',
  BOOK_PERIOD = 'BOOK_PERIOD',
  BIN = 'BIN',
  BINS = 'BINS',
  BOM = 'BOM',
  BOMS = 'BOMS',
  BOM_REVISION = 'BOM_REVISION',
  BOM_REVISIONS = 'BOM_REVISIONS',
  BUDGET = 'BUDGET',
  BUDGETS = 'BUDGETS',
  BUDGET_ACCOUNT = 'BUDGET_ACCOUNT',
  BUDGET_ACCOUNTS = 'BUDGET_ACCOUNTS',
  /**
   * QuickBooks GraphQL `businessPlanningBudget` (Project Budget). Distinct from REST `BUDGET`.
   *
   * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/businessPlanningBudget
   */
  BUSINESS_PLANNING_BUDGET = 'BUSINESS_PLANNING_BUDGET',
  CASH_FLOW_STATEMENT_REPORT = 'CASH_FLOW_STATEMENT_REPORT',
  CHART_ACCOUNT_SETTINGS = 'CHART_ACCOUNT_SETTINGS',
  CHART_ENTITIES = 'CHART_ENTITIES',
  CHART_ENTITY = 'CHART_ENTITY',
  CHART_TRANSACTION = 'CHART_TRANSACTION',
  CHART_TRANSACTIONS = 'CHART_TRANSACTIONS',
  COA_ACCOUNTS = 'COA_ACCOUNTS',
  COA_ACCOUNTS_INCOME_STATEMENT = 'COA_ACCOUNTS_INCOME_STATEMENT',
  COA_FIXED_ASSET_CLASSES = 'COA_FIXED_ASSET_CLASSES',
  CHART_TRANSACTION_MERGE_PREVIEW = 'CHART_TRANSACTION_MERGE_PREVIEW',
  CHECK_IMAGE_DATA = 'CHECK_IMAGE_DATA',
  CATALOG = 'CATALOG',
  CATALOG_ITEM = 'CATALOG_ITEM',
  CATALOG_ITEMS = 'CATALOG_ITEMS',
  LISTINGS_ITEM = 'LISTINGS_ITEM',
  LISTINGS_ITEMS = 'LISTINGS_ITEMS',
  COMPANY = 'COMPANY',
  COMPANY_CONTACT = 'COMPANY_CONTACT',
  COMPANY_CONTACTS = 'COMPANY_CONTACTS',
  COMPANY_IDS = 'COMPANY_IDS',
  COMPANY_INFO = 'COMPANY_INFO',
  COMPANY_INFOS = 'COMPANY_INFOS',
  COMPANY_LOCATION = 'COMPANY_LOCATION',
  COMPANY_LOCATIONS = 'COMPANY_LOCATIONS',
  COMPONENT_INQUIRY = 'COMPONENT_INQUIRY',
  CONTACT = 'CONTACT',
  CONTRACT_ITEM_USAGE_RECORDS = 'CONTRACT_ITEM_USAGE_RECORDS',
  COST_ALLOCATION = 'COST_ALLOCATION',
  COST_ALLOCATIONS = 'COST_ALLOCATIONS',
  CREDIT_MEMO = 'CREDIT_MEMO',
  CREDIT_MEMOS = 'CREDIT_MEMOS',
  NEXT_CREDIT_MEMO_NUMBER = 'NEXT_CREDIT_MEMO_NUMBER',
  CREDIT_MEMO_BULK_SEARCH = 'CREDIT_MEMO_BULK_SEARCH',
  CREDIT_MEMO_PDF = 'CREDIT_MEMO_PDF',
  INVOICE_STATEMENT_CLIENT_PDF = 'INVOICE_STATEMENT_CLIENT_PDF',
  CUSTOMER = 'CUSTOMER',
  CUSTOMER_PAYMENT_METHOD = 'CUSTOMER_PAYMENT_METHOD',
  CUSTOMERS = 'CUSTOMERS',
  CURRENT_BULK_OPERATION = 'CURRENT_BULK_OPERATION',
  CUSTOM_DIMENSION_GROUP = 'CUSTOM_DIMENSION_GROUP',
  CUSTOMER_CURRENCIES = 'CUSTOMER_CURRENCIES',
  CUSTOMER_CURRENCY = 'CUSTOMER_CURRENCY',
  CUSTOM_DIMENSION_GROUPS = 'CUSTOM_DIMENSION_GROUPS',
  CUSTOM_DIMENSION = 'CUSTOM_DIMENSION',
  CUSTOM_DIMENSIONS = 'CUSTOM_DIMENSIONS',
  CUSTOM_DIMENSIONS_BULK_SEARCH = 'CUSTOM_DIMENSIONS_BULK_SEARCH',
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
  CUSTOM_WEBSTORE_ORDER = 'CUSTOM_WEBSTORE_ORDER',
  CUSTOM_WEBSTORE_ORDERS = 'CUSTOM_WEBSTORE_ORDERS',
  MARKETPLACE_ORDER = 'MARKETPLACE_ORDER',
  MARKETPLACE_ORDERS = 'MARKETPLACE_ORDERS',
  OUTBOUND_BULK_ORDER = 'OUTBOUND_BULK_ORDER',
  OUTBOUND_BULK_ORDER_CHANNEL = 'OUTBOUND_BULK_ORDER_CHANNEL',
  OUTBOUND_BULK_ORDER_CHANNELS = 'OUTBOUND_BULK_ORDER_CHANNELS',
  OUTBOUND_BULK_ORDERS = 'OUTBOUND_BULK_ORDERS',
  SHIPMENT_ORDER = 'SHIPMENT_ORDER',
  CUSTOM_FIELD_DEFINITIONS = 'CUSTOM_FIELD_DEFINITIONS',
  DATABASE = 'DATABASE',
  DEBIT_MEMO = 'DEBIT_MEMO',
  DEBIT_MEMOS = 'DEBIT_MEMOS',
  DEBIT_MEMOS_BULK_SEARCH = 'DEBIT_MEMOS_BULK_SEARCH',
  INVOICE_DEFAULT_PAYMENT_AMOUNT = 'INVOICE_DEFAULT_PAYMENT_AMOUNT',
  DEPARTMENT = 'DEPARTMENT',
  DEPARTMENTS = 'DEPARTMENTS',
  DEPARTMENTS_BULK_SEARCH = 'DEPARTMENTS_BULK_SEARCH',
  DEPOSIT = 'DEPOSIT',
  DEPOSITS = 'DEPOSITS',
  DEAL = 'DEAL',
  DELIVERY = 'DELIVERY',
  DELIVERY_TRACKING = 'DELIVERY_TRACKING',
  DEVICE = 'DEVICE',
  DN_HEADER_LEVEL = 'DN_HEADER_LEVEL',
  DIMENSIONS = 'DIMENSIONS',
  DIMENSION_VALUES = 'DIMENSION_VALUES',
  DRAFT_ORDER = 'DRAFT_ORDER',
  DRAFT_ORDERS = 'DRAFT_ORDERS',
  EMAIL = 'EMAIL',
  EMAILS = 'EMAILS',
  EMPLOYEE = 'EMPLOYEE',
  EMPLOYEES = 'EMPLOYEES',
  /**
   * QuickBooks Workforce GraphQL `payrollEmployeeCompensations`.
   * Distinct from REST `EMPLOYEE` / `EMPLOYEES`.
   *
   * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/payrollEmployeeCompensations
   */
  EMPLOYEE_COMPENSATIONS = 'EMPLOYEE_COMPENSATIONS',
  EMPLOYER_INFO = 'EMPLOYER_INFO',
  /**
   * QuickBooks Workforce GraphQL `payrollPayslips`.
   * Distinct from REST `EMPLOYEE` / `EMPLOYEES`.
   *
   * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/payrollPayslips
   */
  PAYROLL_PAYSLIPS = 'PAYROLL_PAYSLIPS',
  /**
   * QuickBooks Workforce GraphQL `workerManagementEmployees` (not REST `Employee` query).
   *
   * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/workerManagementEmployees
   */
  WORKER_MANAGEMENT_EMPLOYEES = 'WORKER_MANAGEMENT_EMPLOYEES',
  /** Shopee payment.get_escrow_detail — an order's escrow/income breakdown. */
  ESCROW_DETAIL = 'ESCROW_DETAIL',
  ESCROW_LIST = 'ESCROW_LIST',
  /** Shopee payment.get_wallet_transaction_list — local-shop wallet ledger rows. */
  WALLET_TRANSACTIONS = 'WALLET_TRANSACTIONS',
  REVERSE_TRACKING_INFO = 'REVERSE_TRACKING_INFO',
  EXCHANGE_RATE = 'EXCHANGE_RATE',
  FINANCE_ACCOUNT_TRANSACTIONS = 'FINANCE_ACCOUNT_TRANSACTIONS',
  FINANCE_TRANSACTION_DETAILS = 'FINANCE_TRANSACTION_DETAILS',
  FBT_INVENTORY = 'FBT_INVENTORY',
  FBT_INVENTORY_RECORDS = 'FBT_INVENTORY_RECORDS',
  FBT_INBOUND_ORDER = 'FBT_INBOUND_ORDER',
  FBT_INBOUND_ORDER_202602 = 'FBT_INBOUND_ORDER_202602',
  FBT_AVAILABLE_INBOUND_METHODS = 'FBT_AVAILABLE_INBOUND_METHODS',
  FBT_INBOUND_METHOD_DETAIL = 'FBT_INBOUND_METHOD_DETAIL',
  FIELD_DEFINITIONS = 'FIELD_DEFINITIONS',
  FILE = 'FILE',
  FILES = 'FILES',
  FIXED_ASSET_CLASS = 'FIXED_ASSET_CLASS',
  FIXED_ASSET_CLASSES = 'FIXED_ASSET_CLASSES',
  FIXED_ASSET = 'FIXED_ASSET',
  FIXED_ASSETS = 'FIXED_ASSETS',
  FIXED_ASSET_AUTOMATION = 'FIXED_ASSET_AUTOMATION',
  FIXED_ASSET_AUTOMATIONS = 'FIXED_ASSET_AUTOMATIONS',
  FIXED_ASSET_AUTOMATION_ACCOUNTS = 'FIXED_ASSET_AUTOMATION_ACCOUNTS',
  FIXED_ASSET_AUTOMATION_MATCH = 'FIXED_ASSET_AUTOMATION_MATCH',
  FIXED_ASSET_AUTOMATION_MATCHES = 'FIXED_ASSET_AUTOMATION_MATCHES',
  FUNCTIONAL_ACKNOWLEDGE = 'FUNCTIONAL_ACKNOWLEDGE',
  FULFILLMENT = 'FULFILLMENT',
  FULFILLMENT_ORDER = 'FULFILLMENT_ORDER',
  FULFILLMENT_ORDERS = 'FULFILLMENT_ORDERS',
  GREEN_LOT_DETAILS = 'GREEN_LOT_DETAILS',
  INVENTORY_SUMMARIES = 'INVENTORY_SUMMARIES',
  ITEM_CASE_ADJUSTMENT = 'ITEM_CASE_ADJUSTMENT',
  FACILITIES = 'FACILITIES',
  INBOUND_ASN = 'INBOUND_ASN',
  INBOUND_ASNS = 'INBOUND_ASNS',
  INBOUND_SHIPPERS = 'INBOUND_SHIPPERS',
  INCOME_STATEMENT = 'INCOME_STATEMENT',
  INTERCOMPANY_ENTITIES = 'INTERCOMPANY_ENTITIES',
  INTERCOMPANY_JOURNAL_ENTRIES = 'INTERCOMPANY_JOURNAL_ENTRIES',
  INTERCOMPANY_JOURNAL_ENTRY = 'INTERCOMPANY_JOURNAL_ENTRY',
  INVENTORY = 'INVENTORY',
  INVENTORY_ADJUSTMENT = 'INVENTORY_ADJUSTMENT',
  INVENTORY_ADJUSTMENT_REASON_CODES = 'INVENTORY_ADJUSTMENT_REASON_CODES',
  INVENTORY_ADJUSTMENTS = 'INVENTORY_ADJUSTMENTS',
  INVENTORY_BLEND = 'INVENTORY_BLEND',
  INVENTORY_BY_BUNDLE = 'INVENTORY_BY_BUNDLE',
  INVENTORY_BY_FACILITY = 'INVENTORY_BY_FACILITY',
  INVENTORY_BY_LISTING = 'INVENTORY_BY_LISTING',
  INVENTORY_BY_LOT = 'INVENTORY_BY_LOT',
  INVENTORY_BY_NETWORK = 'INVENTORY_BY_NETWORK',
  INVENTORY_RECEIVERS = 'INVENTORY_RECEIVERS',
  INVENTORY_GREEN = 'INVENTORY_GREEN',
  INVENTORY_ROAST = 'INVENTORY_ROAST',
  INVENTORY_BALANCE_REPORT = 'INVENTORY_BALANCE_REPORT',
  INVENTORY_COUNT = 'INVENTORY_COUNT',
  INVENTORY_COUNTS = 'INVENTORY_COUNTS',
  INVENTORY_DETAILS = 'INVENTORY_DETAILS',
  INVENTORY_ITEM = 'INVENTORY_ITEM',
  INVENTORY_ITEM_LEVEL = 'INVENTORY_ITEM_LEVEL',
  INVENTORY_ITEMS = 'INVENTORY_ITEMS',
  INVENTORY_LEVEL = 'INVENTORY_LEVEL',
  INVENTORY_LEVEL_BY_FULFILLMENT_CENTER = 'INVENTORY_LEVEL_BY_FULFILLMENT_CENTER',
  INVENTORY_LIST = 'INVENTORY_LIST',
  INVENTORY_NUMBER = 'INVENTORY_NUMBER',
  INVENTORY_NUMBERS = 'INVENTORY_NUMBERS',
  INVENTORY_SHIPMENT = 'INVENTORY_SHIPMENT',
  INVENTORY_SNAPSHOT = 'INVENTORY_SNAPSHOT',
  INVENTORY_STATUS = 'INVENTORY_STATUS',
  INVENTORY_STATUSES = 'INVENTORY_STATUSES',
  ITEM_LOCATIONS = 'ITEM_LOCATIONS',
  INVENTORY_TRANSACTIONS = 'INVENTORY_TRANSACTIONS',
  INVENTORY_REPORT = 'INVENTORY_REPORT',
  INVENTORY_LP_LEVEL = 'INVENTORY_LP_LEVEL',
  INVENTORY_LOT_LEVEL = 'INVENTORY_LOT_LEVEL',
  INVENTORY_TRANSFER = 'INVENTORY_TRANSFER',
  INVENTORY_TRANSFERS = 'INVENTORY_TRANSFERS',
  INVENTORY_ORDER = 'INVENTORY_ORDER',
  INVOICE = 'INVOICE',
  INVOICES = 'INVOICES',
  INVOICE_PDF = 'INVOICE_PDF',
  ITEM = 'ITEM',
  INVOICE_PAYMENTS = 'INVOICE_PAYMENTS',
  ALL_INVOICE_PAYMENTS = 'ALL_INVOICE_PAYMENTS',
  ITEMS = 'ITEMS',
  ITEM_RECEIPT = 'ITEM_RECEIPT',
  ITEM_RECEIPTS = 'ITEM_RECEIPTS',
  ALL_INVENTORY = 'ALL_INVENTORY',
  AWD_INBOUND_SHIPMENT = 'AWD_INBOUND_SHIPMENT',
  AWD_INBOUND_SHIPMENTS = 'AWD_INBOUND_SHIPMENTS',
  AWD_INVENTORY = 'AWD_INVENTORY',
  FBA_INVENTORY = 'FBA_INVENTORY',
  FBA_LEDGER_DETAIL_VIEW = 'FBA_LEDGER_DETAIL_VIEW',
  FULFILLED_SHIPMENTS = 'FULFILLED_SHIPMENTS',
  INBOUND_OPERATION_STATUS = 'INBOUND_OPERATION_STATUS',
  INBOUND_PLAN = 'INBOUND_PLAN',
  INBOUND_PLANS = 'INBOUND_PLANS',
  FBA_INBOUND_SHIPMENT = 'FBA_INBOUND_SHIPMENT',
  MFN_SHIPMENT = 'MFN_SHIPMENT',
  INCOME_STATEMENT_REPORT = 'INCOME_STATEMENT_REPORT',
  ITEMIZED_SHIPMENT_COSTS = 'ITEMIZED_SHIPMENT_COSTS',
  JOURNAL = 'JOURNAL',
  JOURNAL_ENTRY = 'JOURNAL_ENTRY',
  JOURNAL_ENTRIES = 'JOURNAL_ENTRIES',
  JOURNAL_ENTRIES_REPORT = 'JOURNAL_ENTRIES_REPORT',
  MANUAL_JOURNAL = 'MANUAL_JOURNAL',
  ITEM_MASTER_INQUIRY = 'ITEM_MASTER_INQUIRY',
  ITEMS_RECOMMENDATIONS_CATALOG = 'ITEMS_RECOMMENDATIONS_CATALOG',
  GENERAL_LEDGER_REPORT = 'GENERAL_LEDGER_REPORT',
  GENERAL_LEDGER_TRANSACTION_RECONCILIATION = 'GENERAL_LEDGER_TRANSACTION_RECONCILIATION',
  GENERIC_QUERY = 'GENERIC_QUERY',
  HEALTH = 'HEALTH',
  LABEL = 'LABEL',
  LABELS = 'LABELS',
  LINKED_ACCOUNTS = 'LINKED_ACCOUNTS',
  LOCATION = 'LOCATION',
  LOCATIONS = 'LOCATIONS',
  LOT_NUMBERED_INVENTORY_ITEM = 'LOT_NUMBERED_INVENTORY_ITEM',
  LOT_NUMBERED_INVENTORY_ITEMS = 'LOT_NUMBERED_INVENTORY_ITEMS',
  LOT_SERIAL_AVAILABILITY = 'LOT_SERIAL_AVAILABILITY',
  NON_INVENTORY_PURCHASE_ITEM = 'NON_INVENTORY_PURCHASE_ITEM',
  NON_INVENTORY_PURCHASE_ITEMS = 'NON_INVENTORY_PURCHASE_ITEMS',
  NON_INVENTORY_RESALE_ITEM = 'NON_INVENTORY_RESALE_ITEM',
  NON_INVENTORY_RESALE_ITEMS = 'NON_INVENTORY_RESALE_ITEMS',
  NON_INVENTORY_SALE_ITEM = 'NON_INVENTORY_SALE_ITEM',
  NON_INVENTORY_SALE_ITEMS = 'NON_INVENTORY_SALE_ITEMS',
  OCEAN_IMPORT_HBL = 'OCEAN_IMPORT_HBL',
  OCEAN_IMPORT_HBLS = 'OCEAN_IMPORT_HBLS',
  OCEAN_IMPORT_MBL = 'OCEAN_IMPORT_MBL',
  ORDER = 'ORDER',
  ORDER_CHANGE = 'ORDER_CHANGE',
  ORDER_LINE = 'ORDER_LINE',
  ORDER_LINES = 'ORDER_LINES',
  ORDER_FINANCIAL_EVENTS = 'ORDER_FINANCIAL_EVENTS',
  FINANCIAL_EVENTS = 'FINANCIAL_EVENTS',
  ORDER_STATEMENT_TRANSACTIONS = 'ORDER_STATEMENT_TRANSACTIONS',
  ORDERS = 'ORDERS',
  ORDERS_V2026 = 'ORDERS_V2026',
  ORDERS_SHIPPED = 'ORDERS_SHIPPED',
  SEARCH_ORDERS = 'SEARCH_ORDERS',
  SERIALIZED_INVENTORY_ITEM = 'SERIALIZED_INVENTORY_ITEM',
  SERIALIZED_INVENTORY_ITEMS = 'SERIALIZED_INVENTORY_ITEMS',
  ORDER_DC = 'ORDER_DC',
  ORDER_STATUS = 'ORDER_STATUS',
  ORDER_SUMMARY = 'ORDER_SUMMARY',
  ORGANIZATION = 'ORGANIZATION',
  PACKAGE = 'PACKAGE',
  PACKAGING = 'PACKAGING',
  PACKAGING_CASE_ADJUSTMENT = 'PACKAGING_CASE_ADJUSTMENT',
  PRODUCT_CATALOG = 'PRODUCT_CATALOG',
  PAYABLE = 'PAYABLE',
  PAYMENT = 'PAYMENT',
  PAYMENTS_BALANCE_TRANSACTIONS = 'PAYMENTS_BALANCE_TRANSACTIONS',
  PAYOUT_STATUS = 'PAYOUT_STATUS',
  PRICE_AND_AVAILABILITY = 'PRICE_AND_AVAILABILITY',
  PRICE_LIST = 'PRICE_LIST',
  PRICE_LISTS = 'PRICE_LISTS',
  PRODUCT = 'PRODUCT',
  PRODUCT_BUNDLE = 'PRODUCT_BUNDLE',
  PRODUCT_BUNDLES = 'PRODUCT_BUNDLES',
  PRODUCTS = 'PRODUCTS',
  PRODUCT_VARIANTS = 'PRODUCT_VARIANTS',
  PROJECT = 'PROJECT',
  PROJECTS = 'PROJECTS',
  PROJECT_MANAGEMENT_SETTINGS = 'PROJECT_MANAGEMENT_SETTINGS',
  PROPERTIES = 'PROPERTIES',
  STYLE = 'STYLE',
  STYLES = 'STYLES',
  MATERIAL = 'MATERIAL',
  MATERIALS = 'MATERIALS',
  COLOR = 'COLOR',
  COLORS = 'COLORS',
  DIRECTORY = 'DIRECTORY',
  DIRECTORIES = 'DIRECTORIES',
  DIGITAL_ASSET = 'DIGITAL_ASSET',
  DIGITAL_ASSETS = 'DIGITAL_ASSETS',
  PURCHASE = 'PURCHASE',
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  PURCHASE_ORDER_RECEIVING_HISTORY = 'PURCHASE_ORDER_RECEIVING_HISTORY',
  PURCHASE_ORDERS = 'PURCHASE_ORDERS',
  PURCHASES = 'PURCHASES',
  RESTOCK_INVENTORY_RECOMMENDATIONS = 'RESTOCK_INVENTORY_RECOMMENDATIONS',
  RECEIPT = 'RECEIPT',
  RECEIPT_RC = 'RECEIPT_RC',
  RECEIPTS = 'RECEIPTS',
  RECEIPT_CONFIRMATIONS = 'RECEIPT_CONFIRMATIONS',
  RECEIVING = 'RECEIVING',
  RECEIVING_BOXES = 'RECEIVING_BOXES',
  RECEIVING_ITEMS = 'RECEIVING_ITEMS',
  RECEIVING_EVENTS = 'RECEIVING_EVENTS',
  RECEIVING_HISTORY = 'RECEIVING_HISTORY',
  RECEIVING_LABELS = 'RECEIVING_LABELS',
  RECEIVINGS = 'RECEIVINGS',
  REFUND_RECEIPT = 'REFUND_RECEIPT',
  RECEIPT_HEADER = 'RECEIPT_HEADER',
  RECEIPT_DETAIL = 'RECEIPT_DETAIL',
  REIMBURSEMENT = 'REIMBURSEMENT',
  REIMBURSEMENTS = 'REIMBURSEMENTS',
  REPORT = 'REPORT',
  REPORT_DOCUMENT = 'REPORT_DOCUMENT',
  REPORTS = 'REPORTS',
  REVENUE_TRANSACTION = 'REVENUE_TRANSACTION',
  REVENUE_TRANSACTIONS = 'REVENUE_TRANSACTIONS',
  REFUND = 'REFUND',
  RETURN = 'RETURN',
  RETURNS = 'RETURNS',
  RETURN_ITEMS = 'RETURN_ITEMS',
  REVERSE_ORDER = 'REVERSE_ORDER',
  REVERSE_ORDERS = 'REVERSE_ORDERS',
  SALES_ORDER = 'SALES_ORDER',
  SALES_ORDERS = 'SALES_ORDERS',
  SALES_RECEIPT = 'SALES_RECEIPT',
  SHIPMENT = 'SHIPMENT',
  SHIPMENTS = 'SHIPMENTS',
  SHIPMENT_ITEMS = 'SHIPMENT_ITEMS',
  SHIPMENT_ITEMS_V2024 = 'SHIPMENT_ITEMS_V2024',
  SHIPMENT_STATUS = 'SHIPMENT_STATUS',
  SHIPMENT_PROVIDERS = 'SHIPMENT_PROVIDERS',
  SHIPMENT_TYPES = 'SHIPMENT_TYPES',
  SHIP_OPTIONS = 'SHIP_OPTIONS',
  PLACEMENT_OPTIONS = 'PLACEMENT_OPTIONS',
  CONTRACTED_CARRIER_METHODS = 'CONTRACTED_CARRIER_METHODS',
  CONTRACTED_CARRIER_SERVICE_METHODS = 'CONTRACTED_CARRIER_SERVICE_METHODS',
  SERVICE_LEVELS = 'SERVICE_LEVELS',
  SHIPPING_METHODS = 'SHIPPING_METHODS',
  SHIPPING_PARAMETER = 'SHIPPING_PARAMETER',
  SHIPPING_RATE = 'SHIPPING_RATE',
  SHIPPING_RATES = 'SHIPPING_RATES',
  SHIPMENT_CONFIRMATION = 'SHIPMENT_CONFIRMATION',
  SHIPPING_LABEL = 'SHIPPING_LABEL',
  SPLIT_ORDERS = 'SPLIT_ORDERS',
  STOCK = 'STOCK',
  STOCK_ADJUSTMENT = 'STOCK_ADJUSTMENT',
  ALL_STOCK = 'ALL_STOCK',
  STOCK_LEVELS = 'STOCK_LEVELS',
  ALL_STOCK_LEVELS = 'ALL_STOCK_LEVELS',
  STOCK_MOVEMENT = 'STOCK_MOVEMENT',
  STOCK_ON_HAND_REPORT = 'STOCK_ON_HAND_REPORT',
  STOCK_ON_HAND_REPORT_RUN = 'STOCK_ON_HAND_REPORT_RUN',
  STOCK_TOTAL = 'STOCK_TOTAL',
  STOCK_TOTALS = 'STOCK_TOTALS',
  SHIPMENT_LABELS = 'SHIPMENT_LABELS',
  SHIPMENT_LABEL_FORMAT = 'SHIPMENT_LABEL_FORMAT',
  SHIPPING_PROVIDERS = 'SHIPPING_PROVIDERS',
  SKU = 'SKU',
  SKUS = 'SKUS',
  STATEMENT = 'STATEMENT',
  STATEMENTS = 'STATEMENTS',
  GL_ACCOUNT = 'GL_ACCOUNT',
  GL_ACCOUNTS = 'GL_ACCOUNTS',
  ACCOUNTING_FIELD = 'ACCOUNTING_FIELD',
  ACCOUNTING_FIELDS = 'ACCOUNTING_FIELDS',
  SUBSIDIARY = 'SUBSIDIARY',
  SUBSIDIARIES = 'SUBSIDIARIES',
  SUPPLIERS = 'SUPPLIERS',
  TAX_AGENCY = 'TAX_AGENCY',
  TAX_AGENCIES = 'TAX_AGENCIES',
  TAX_RATE = 'TAX_RATE',
  TAX_RATES = 'TAX_RATES',
  TAX_CODE = 'TAX_CODE',
  TAX_CODES = 'TAX_CODES',
  TAX_CLASSIFICATION = 'TAX_CLASSIFICATION',
  TAX_CLASSIFICATIONS = 'TAX_CLASSIFICATIONS',
  TAX_PAYMENT = 'TAX_PAYMENT',
  TAX_PAYMENTS = 'TAX_PAYMENTS',
  TRANSACTION = 'TRANSACTION',
  TRANSACTION_FILE = 'TRANSACTION_FILE',
  TRANSACTION_MATCHES_COUNT = 'TRANSACTION_MATCHES_COUNT',
  TRANSACTION_MATCH = 'TRANSACTION_MATCH',
  TRANSACTION_MATCHES = 'TRANSACTION_MATCHES',
  TRANSACTION_MESSAGE = 'TRANSACTION_MESSAGE',
  TRANSACTIONS = 'TRANSACTIONS',
  INBOX_TRANSACTIONS = 'INBOX_TRANSACTIONS',
  TRANSFER = 'TRANSFER',
  TRANSFERS = 'TRANSFERS',
  TRACKER = 'TRACKER',
  TRACKING = 'TRACKING',
  TRACKING_RETURN_REPORT = 'TRACKING_RETURN_REPORT',
  TRANSFER_ORDERS = 'TRANSFER_ORDERS',
  TRIAL_BALANCE = 'TRIAL_BALANCE',
  TRIAL_BALANCE_REPORT = 'TRIAL_BALANCE_REPORT',
  QBO_TRIAL_BALANCE_REPORT = 'QBO_TRIAL_BALANCE_REPORT',
  TAX_SUMMARY_REPORT = 'TAX_SUMMARY_REPORT',
  HEALTH_CHECK = 'HEALTH_CHECK',
  VENDOR = 'VENDOR',
  VENDOR_CREDIT = 'VENDOR_CREDIT',
  VENDOR_CREDITS = 'VENDOR_CREDITS',
  VENDOR_EXPENSES = 'VENDOR_EXPENSES',
  VENDOR_CUSTOM_FIELD_1 = 'VENDOR_CUSTOM_FIELD_1',
  VENDOR_CUSTOM_FIELD_1_DETAIL = 'VENDOR_CUSTOM_FIELD_1_DETAIL',
  VENDOR_SUMMARIES = 'VENDOR_SUMMARIES',
  VENDORS = 'VENDORS',
  VENDORS_BULK_SEARCH = 'VENDORS_BULK_SEARCH',
  VENDOR_BANK = 'VENDOR_BANK',
  VENDOR_CONFIGURATION = 'VENDOR_CONFIGURATION',
  VENDOR_CONTACTS = 'VENDOR_CONTACTS',
  VENDOR_REQUIRED_INFORMATION = 'VENDOR_REQUIRED_INFORMATION',
  WAREHOUSES = 'WAREHOUSES',
  WORK_ORDER = 'WORK_ORDER',
  WORK_ORDERS = 'WORK_ORDERS',
  WORK_ORDER_CLOSE = 'WORK_ORDER_CLOSE',
  WORK_ORDER_CLOSES = 'WORK_ORDER_CLOSES',
  WORK_ORDER_COMPLETION = 'WORK_ORDER_COMPLETION',
  WORK_ORDER_COMPLETIONS = 'WORK_ORDER_COMPLETIONS',
  WORK_ORDER_ISSUE = 'WORK_ORDER_ISSUE',
  WORK_ORDER_ISSUES = 'WORK_ORDER_ISSUES',
  BILLING_ACCOUNT = 'BILLING_ACCOUNT',
  BILLING_ACCOUNTS = 'BILLING_ACCOUNTS',
  VENDOR_CATEGORY = 'VENDOR_CATEGORY',
  VENDOR_CATEGORIES = 'VENDOR_CATEGORIES',
  VENDOR_PAYMENT = 'VENDOR_PAYMENT',
  VENDOR_PAYMENTS = 'VENDOR_PAYMENTS',
  VENDOR_BILL = 'VENDOR_BILL',
  VENDOR_BILLS = 'VENDOR_BILLS',
  CUSTOMER_CATEGORY = 'CUSTOMER_CATEGORY',
  CUSTOMER_CATEGORIES = 'CUSTOMER_CATEGORIES',
  CUSTOMER_DEPOSIT = 'CUSTOMER_DEPOSIT',
  CUSTOMER_DEPOSITS = 'CUSTOMER_DEPOSITS',
  CUSTOMER_PAYMENT = 'CUSTOMER_PAYMENT',
  CUSTOMER_PAYMENTS = 'CUSTOMER_PAYMENTS',
  CUSTOMER_REFUND = 'CUSTOMER_REFUND',
  CUSTOMER_REFUNDS = 'CUSTOMER_REFUNDS',
  CUSTOMER_STATUS = 'CUSTOMER_STATUS',
  CUSTOMER_STATUSES = 'CUSTOMER_STATUSES',
  DESCRIPTION_ITEM = 'DESCRIPTION_ITEM',
  DESCRIPTION_ITEMS = 'DESCRIPTION_ITEMS',
  DISCOUNT_ITEM = 'DISCOUNT_ITEM',
  DISCOUNT_ITEMS = 'DISCOUNT_ITEMS',
  DOWNLOAD_ITEM = 'DOWNLOAD_ITEM',
  DOWNLOAD_ITEMS = 'DOWNLOAD_ITEMS',
  GIFT_CERTIFICATE_ITEM = 'GIFT_CERTIFICATE_ITEM',
  GIFT_CERTIFICATE_ITEMS = 'GIFT_CERTIFICATE_ITEMS',
  ITEM_ACCOUNT_MAPPING = 'ITEM_ACCOUNT_MAPPING',
  ITEM_ACCOUNT_MAPPINGS = 'ITEM_ACCOUNT_MAPPINGS',
  ITEM_FULFILLMENT = 'ITEM_FULFILLMENT',
  ITEM_FULFILLMENTS = 'ITEM_FULFILLMENTS',
  ITEM_GROUP = 'ITEM_GROUP',
  ITEM_GROUPS = 'ITEM_GROUPS',
  ITEM_REVISION = 'ITEM_REVISION',
  ITEM_REVISIONS = 'ITEM_REVISIONS',
  KIT_ITEM = 'KIT_ITEM',
  KIT_ITEMS = 'KIT_ITEMS',
  MARKUP_ITEM = 'MARKUP_ITEM',
  MARKUP_ITEMS = 'MARKUP_ITEMS',
  OTHER_CHARGE_PURCHASE_ITEM = 'OTHER_CHARGE_PURCHASE_ITEM',
  OTHER_CHARGE_PURCHASE_ITEMS = 'OTHER_CHARGE_PURCHASE_ITEMS',
  OTHER_CHARGE_RESALE_ITEM = 'OTHER_CHARGE_RESALE_ITEM',
  OTHER_CHARGE_RESALE_ITEMS = 'OTHER_CHARGE_RESALE_ITEMS',
  OTHER_CHARGE_SALE_ITEM = 'OTHER_CHARGE_SALE_ITEM',
  OTHER_CHARGE_SALE_ITEMS = 'OTHER_CHARGE_SALE_ITEMS',
  PAYMENT_ITEM = 'PAYMENT_ITEM',
  PAYMENT_ITEMS = 'PAYMENT_ITEMS',
  PAYROLL_ITEM = 'PAYROLL_ITEM',
  PAYROLL_ITEMS = 'PAYROLL_ITEMS',
  SALES_TAX_ITEM = 'SALES_TAX_ITEM',
  SALES_TAX_ITEMS = 'SALES_TAX_ITEMS',
  SERVICE_PURCHASE_ITEM = 'SERVICE_PURCHASE_ITEM',
  SERVICE_PURCHASE_ITEMS = 'SERVICE_PURCHASE_ITEMS',
  SERVICE_RESALE_ITEM = 'SERVICE_RESALE_ITEM',
  SERVICE_RESALE_ITEMS = 'SERVICE_RESALE_ITEMS',
  SERVICE_SALE_ITEM = 'SERVICE_SALE_ITEM',
  SERVICE_SALE_ITEMS = 'SERVICE_SALE_ITEMS',
  SHIP_ITEM = 'SHIP_ITEM',
  SHIP_ITEMS = 'SHIP_ITEMS',
  SUBTOTAL_ITEM = 'SUBTOTAL_ITEM',
  SUBTOTAL_ITEMS = 'SUBTOTAL_ITEMS',
  CLASSIFICATION = 'CLASSIFICATION',
  CLASSIFICATIONS = 'CLASSIFICATIONS',
  ADVANCED_SHIPPING_NOTICE = 'ADVANCED_SHIPPING_NOTICE',
}

export enum ArrayOperation {
  LOOP = 'LOOP',
  INDEX = 'INDEX',
}

export enum LabelFormat {
  PDF = 'PDF',
  ZPL = 'ZPL',
  EPL2 = 'EPL2',
}

export const WRITE_INTEGRATION_TYPES = [
  IntegrationType.A2B_FULFILLMENT,
  IntegrationType.AIRTABLE,
  IntegrationType.AMAZON_SELLER,
  IntegrationType.ARAMEX,
  IntegrationType.BEPRODUCT,
  IntegrationType.BILL,
  IntegrationType.BYRD,
  IntegrationType.BREX,
  IntegrationType.CAMPFIRE,
  IntegrationType.CRSTL,
  IntegrationType.DOSS_BANKING,
  IntegrationType.DOSS_BILL_PAY,
  IntegrationType.EASYPOST,
  IntegrationType.ECHO_SHIP,
  IntegrationType.EXTENSIV,
  IntegrationType.FIRSTBASE,
  IntegrationType.FLEXPORT,
  IntegrationType.FORTY_SEAS,
  IntegrationType.FOSDICK,
  IntegrationType.FTP,
  IntegrationType.G2MINT,
  IntegrationType.GMAIL,
  IntegrationType.GNOSIS,
  IntegrationType.HUBSPOT,
  IntegrationType.INGRAM_MICRO,
  IntegrationType.JAY_GROUP,
  IntegrationType.JIGSAW,
  IntegrationType.JAZZ,
  IntegrationType.LAZADA,
  IntegrationType.LOCAD,
  IntegrationType.LOOP_RETURNS,
  IntegrationType.MAINFREIGHT,
  IntegrationType.MARKETTIME,
  IntegrationType.MAYA,
  IntegrationType.MEDUSA,
  IntegrationType.MISFITS_MARKET,
  IntegrationType.MINTSOFT,
  IntegrationType.NAVISPHERE,
  IntegrationType.NETSUITE,
  IntegrationType.NOK_RECOMMERCE,
  IntegrationType.ONE_WORLD_DIRECT,
  IntegrationType.ORDERFUL,
  IntegrationType.OUTLOOK,
  IntegrationType.PRO_PAC,
  IntegrationType.QUICKBOOKS,
  IntegrationType.RAMP,
  IntegrationType.RILLET,
  IntegrationType.ROUTABLE,
  IntegrationType.SHIPBOB,
  IntegrationType.SHIPMONK,
  IntegrationType.SHIPHERO,
  IntegrationType.SHIPSTATION,
  IntegrationType.SHIPSIDEKICK,
  IntegrationType.SNAPFULFIL,
  IntegrationType.SHOPEE,
  IntegrationType.SHOPIFY,
  IntegrationType.SPS_COMMERCE,
  IntegrationType.STORD,
  IntegrationType.STRIPE,
  IntegrationType.TD_SYNNEX,
  IntegrationType.TD_SYNNEX_XML,
  IntegrationType.THINK_LOGISTICS,
  IntegrationType.TIKTOK_SHOP,
  IntegrationType.TQL_FREIGHT,
  IntegrationType.UNIS,
  IntegrationType.USA_SUPPLY,
  IntegrationType.VERACORE,
  IntegrationType.WALMART,
  IntegrationType.WOO_COMMERCE,
  IntegrationType.XERO,
  IntegrationType.HTTP_ENDPOINT,
]
export type WriteIntegrationTypes = (typeof WRITE_INTEGRATION_TYPES)[number]

export const READ_INTEGRATION_TYPES = [
  IntegrationType.A2B_FULFILLMENT,
  IntegrationType.AMAZON_SELLER,
  IntegrationType.ARAMEX,
  IntegrationType.BRND_SUPPLIER,
  IntegrationType.BRND_WHOLESALE,
  IntegrationType.BYRD,
  IntegrationType.BEPRODUCT,
  IntegrationType.BILL,
  IntegrationType.BUHO,
  IntegrationType.CAMPFIRE,
  IntegrationType.CARTONCLOUD,
  IntegrationType.CROPSTER,
  IntegrationType.CRSTL,
  IntegrationType.D2,
  IntegrationType.DOSS_BILL_PAY,
  IntegrationType.DOSS_EXCHANGE_RATES,
  IntegrationType.EASYPOST,
  IntegrationType.EXTENSIV,
  IntegrationType.FAIRE,
  IntegrationType.FIRSTBASE,
  IntegrationType.FLEXPORT,
  IntegrationType.FOSDICK,
  IntegrationType.DEPOSCO,
  IntegrationType.FTP,
  IntegrationType.G2MINT,
  IntegrationType.GOFREIGHT,
  IntegrationType.GMAIL,
  IntegrationType.HUBSPOT,
  IntegrationType.INGRAM_MICRO,
  IntegrationType.JAY_GROUP,
  IntegrationType.JAZZ,
  IntegrationType.JIGSAW,
  IntegrationType.LAZADA,
  IntegrationType.LOGIWA,
  IntegrationType.LOCAD,
  IntegrationType.LOOP_RETURNS,
  IntegrationType.MAINFREIGHT,
  IntegrationType.MARKETTIME,
  IntegrationType.MAYA,
  IntegrationType.MEDUSA,
  IntegrationType.MINTSOFT,
  IntegrationType.MISFITS_MARKET,
  IntegrationType.NETSUITE,
  IntegrationType.NOK_RECOMMERCE,
  IntegrationType.NTG_WMS,
  IntegrationType.ONE_WORLD_DIRECT,
  IntegrationType.ORDERFUL,
  IntegrationType.POSTGRES,
  IntegrationType.PRO_PAC,
  IntegrationType.QUICKBOOKS,
  IntegrationType.RAMP,
  IntegrationType.RAPID_PACK,
  IntegrationType.RILLET,
  IntegrationType.ROUTABLE,
  IntegrationType.SALSIFY,
  IntegrationType.SHIPBOB,
  IntegrationType.SHIPMONK,
  IntegrationType.SHIPHERO,
  IntegrationType.SHIPSIDEKICK,
  IntegrationType.SHIPSTATION,
  IntegrationType.SNAPFULFIL,
  IntegrationType.SHIPWIRE,
  IntegrationType.SHOPEE,
  IntegrationType.SHOPIFY,
  IntegrationType.SILQ,
  IntegrationType.SPS_COMMERCE,
  IntegrationType.STORD,
  IntegrationType.TD_SYNNEX,
  IntegrationType.TD_SYNNEX_XML,
  IntegrationType.THINK_LOGISTICS,
  IntegrationType.TIKTOK_SHOP,
  IntegrationType.TOAST,
  IntegrationType.TQL_FREIGHT,
  IntegrationType.UNIS,
  IntegrationType.VERACORE,
  IntegrationType.WAREHANCE,
  IntegrationType.WALMART,
  IntegrationType.WESTERN_POST,
  IntegrationType.XERO,
  IntegrationType.WOO_COMMERCE,
]
export type ReadIntegrationTypes = (typeof READ_INTEGRATION_TYPES)[number]

/**
 * The base type used by Yup for mixed schemas, which is any defined non-null value.
 *
 * See: https://github.com/jquense/yup#mixed
 */
type YupMixedBasic = NonNullable<unknown>

/**
 * Creates a Yup schema for an array mapping, which is an object with the following properties:
 *  - `operation` - The array operation to perform
 *  - `arrayPath` - The path to the array
 *  - `index` - The index to resolve to if the operation is `INDEX`
 *  - `mappings` - The mappings to apply to each item in the array, this is an object whose key is
 *        the field of the array item being set, and the value is the mapping to use to retrieve the
 *        value to set for that field.
 *
 * @param itemsSchema - An optional Yup schema for the items being mapped in the array
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup generics requires using `any`
export function getMappedArraySchema<T extends YupMixedBasic = any>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup generics requires using `any`
  itemsSchema: yup.Lazy<T> | yup.Schema<T> = yup.mixed<any>(),
): yup.ObjectSchema<{
  operation: NonNullable<ArrayOperation | undefined>
  arrayPath: string
  index: number | undefined
  mappings: T & YupMixedBasic
}> {
  return yup.object({
    operation: yup.string().oneOf(Object.values(ArrayOperation)).required(),
    arrayPath: yup.string().required(),
    index: yup.number().when('operation', {
      is: ArrayOperation.INDEX,
      then: (schema) => schema.required(),
    }),
    mappings: yup.mixed<T>((value): value is T => itemsSchema.isValidSync(value)).required(),
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup generics requires using `any`
export type MappedArray<T extends YupMixedBasic = any> = yup.InferType<
  ReturnType<typeof getMappedArraySchema<T>>
>

// Utilities
const mappedActionDataPrimitiveSchema =
  getMappedArraySchema<ActionDataPrimitive>(actionDataPrimitiveSchema).required()

/**
 * Schema for array operations in update steps. Supports both:
 * - Legacy format: direct ActionData (defaults to 'replace')
 * - New format: { operation: 'append' | 'replace' | 'remove', value: ActionData }
 */
/**
 * Schema for array operation wrappers used in UPDATE_TABLE_ROW action steps.
 *
 * The wrapper format is: { operation: 'append' | 'replace' | 'remove' | null, value: unknown }
 *
 * Operation semantics:
 * - 'append': Add items to existing array (merge and deduplicate)
 * - 'remove': Remove specified items from existing array
 * - 'replace': Replace entire array with new value (default behavior)
 * - null/undefined: Use default behavior (replace)
 *
 * The operation field is nullable to support the default behavior when users don't
 * explicitly select an operation. When null, it behaves as 'replace'.
 *
 * The value field is validated lazily and can be optional to handle incomplete UI state.
 * Incomplete operations (operation set but no value) are cleaned up in the resolver layer
 * via cleanupIncompleteArrayOperations before saving to the database.
 */
const arrayOperationWrapperSchema = yup.object({
  operation: yup.string().oneOf(['append', 'replace', 'remove']).nullable(),
  value: yup.lazy((value) => {
    if (value === undefined || value === null) {
      return yup.mixed().optional()
    }
    if (mappedActionDataPrimitiveSchema.isValidSync(value)) {
      return mappedActionDataPrimitiveSchema
    }
    if (getMappedStringArraySchema().isValidSync(value)) {
      return getMappedStringArraySchema()
    }
    return actionDataSchema
  }),
})

const rowCellSelectionSchema = yup.mixed<
  MappedArray<ActionDataPrimitive> | ActionData | yup.InferType<typeof arrayOperationWrapperSchema>
>(
  (
    value,
  ): value is
    | MappedArray<ActionDataPrimitive>
    | ActionData
    | yup.InferType<typeof arrayOperationWrapperSchema> =>
    mappedActionDataPrimitiveSchema.isValidSync(value) ||
    actionDataSchema.isValidSync(value) ||
    getMappedStringArraySchema().isValidSync(value) ||
    arrayOperationWrapperSchema.isValidSync(value),
)

/**
 * Creates a Yup schema for an array mapping where the array items are strings. Since this is an
 * array of primitives, the mappings can only contain a "root" key.
 */
export function getMappedStringArraySchema(): yup.ObjectSchema<{
  operation: NonNullable<ArrayOperation | undefined>
  arrayPath: string
  index: number | undefined
  mappings: { root: string }
}> {
  return getMappedArraySchema(yup.object({ root: stringActionDataSchema.required() }))
}

export type MappedStringArray = yup.InferType<ReturnType<typeof getMappedStringArraySchema>>

/**
 * Creates a Yup schema for an array mapping where the array items are numbers. Since this is an
 * array of primitives, the mappings can only contain a "root" key.
 */
export function getMappedNumberArraySchema(): yup.ObjectSchema<{
  operation: NonNullable<ArrayOperation | undefined>
  arrayPath: string
  index: number | undefined
  // The mapping could be a number value, or a string input mapping that resolves to a number
  mappings: { root: string | number }
}> {
  return getMappedArraySchema(
    yup.object({
      root: yup
        .mixed<string | number>(
          (value): value is string | number =>
            stringActionDataSchema.isValidSync(value) || numberActionDataSchema.isValidSync(value),
        )
        .required(),
    }),
  )
}

export type MappedNumberArray = yup.InferType<ReturnType<typeof getMappedNumberArraySchema>>

/**
 * Creates a Yup schema for an array mapping where the array items are booleans. Since this is an
 * array of primitives, the mappings can only contain a "root" key.
 */
export function getMappedBooleanArraySchema(): yup.ObjectSchema<{
  operation: NonNullable<ArrayOperation | undefined>
  arrayPath: string
  index: number | undefined
  // The mapping could be a boolean value, or a string input mapping that resolves to a boolean
  mappings: { root: string | boolean }
}> {
  return getMappedArraySchema(
    yup.object({
      root: yup
        .mixed<string | boolean>(
          (value): value is string | boolean =>
            stringActionDataSchema.isValidSync(value) || booleanActionDataSchema.isValidSync(value),
        )
        .required(),
    }),
  )
}

export type MappedBooleanArray = yup.InferType<ReturnType<typeof getMappedBooleanArraySchema>>

/**
 * Schema for the "cells" input object in table row insert or update steps. The object can contain
 * any string key for the column ID, and the value is an object containing either a cell or a
 * lookup cell ID.
 */
export const rowCellsSelectionsSchema = yup.lazy((obj) =>
  yup.object(_.mapValues(obj, () => rowCellSelectionSchema.defined().nullable())).required(),
)
export type RowCellsSelections = yup.InferType<typeof rowCellsSelectionsSchema>

const mappedRowsSelectionsSchema =
  getMappedArraySchema<RowCellsSelections>(rowCellsSelectionsSchema).required()
const multipleRowsCellsSelectionsSchema = yup.array().of(rowCellsSelectionsSchema).required()

/**
 * Schema for a valid cell value that can be either ActionData, LookupCellData,
 * array operation wrapper (for UPDATE_TABLE_ROW), or null
 */
export const cellValueSchema = yup
  .mixed()
  .test(
    'is-valid-cell-value',
    'Must be either ActionData, LookupCellData, ArrayOperationWrapper, or null',
    (value) =>
      _.isNull(value) ||
      actionDataSchema.isValidSync(value) ||
      lookupCellDataSchema.isValidSync(value) ||
      getMappedStringArraySchema().isValidSync(value) ||
      arrayOperationWrapperSchema.isValidSync(value),
  )
  .nullable()
  .defined()

/**
 * Schema for the "cells" input object in table row insert or update steps. The object can contain
 * any string key for the column ID, and the value is one of the following:
 *  - A primitive value
 *  - An array of primitive values
 *  - A lookup cell data object
 *  - a null value
 */
export const rowCellsInputSchema = yup.lazy((obj) =>
  yup
    .object()
    .shape(_.mapValues(obj, () => cellValueSchema))
    .required(),
)
export type RowCellsInput = yup.InferType<typeof rowCellsInputSchema>

// Schemas

type MappedFormulaSelectionValue = { value: string }
type FormulaSelectionValue =
  | MappedArray<MappedFormulaSelectionValue>
  | (string | number | FormulaValue)[]
type FormulaValue = {
  operator: FormulaOperator
  formulaValues: FormulaSelectionValue
}

/**
 * Selections data schema for a formula action step.
 * Allows an array of strings (mapping or date strings) or numbers or a mapped array of strings as formula values inputs.
 */
export const formulaSelectionsExpressionSchema = yup
  .object({
    operator: yup.string().oneOf(Object.values(FormulaOperator)).required(),
    formulaValues: yup
      .mixed<FormulaSelectionValue>(
        (value): value is FormulaSelectionValue =>
          getMappedArraySchema<MappedFormulaSelectionValue>(
            yup.object({
              value: stringActionDataSchema.required(),
            }),
          )
            .required()
            .isValidSync(value) ||
          yup.array().of(yup.mixed<string | number>().required()).required().isValidSync(value) ||
          yup
            .object({
              operator: yup.string().oneOf(Object.values(FormulaOperator)).required(),
              formulaValues: yup.array().of(yup.mixed<string | number>().required()).required(),
            })
            .isValidSync(value),
      )
      .required(),
  })
  .strict()
export type FormulaSelectionsExpression = yup.InferType<typeof formulaSelectionsExpressionSchema>

export const formulaSelectionsSchema = yup.object({
  type: yup
    .string()
    .oneOf([ActionStepType.FORMULA] as const)
    .required(),
  formula: formulaSelectionsExpressionSchema.required(),
})

export const insertTableRowSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.INSERT_TABLE_ROW]).required(),
  tableId: yup.string().required(),
  parentRowId: yup.string(),
  rows: yup
    .mixed<MappedArray<RowCellsInput> | RowCellsSelections[]>(
      (value): value is MappedArray<RowCellsInput> | RowCellsSelections[] => {
        return (
          mappedRowsSelectionsSchema.isValidSync(value) ||
          multipleRowsCellsSelectionsSchema.isValidSync(value)
        )
      },
    )
    .required(),
  waitForAsync: yup.boolean().notRequired(),
  waitForAsyncTimeout: yup.number().notRequired(),
})

export const updateTableRowSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.UPDATE_TABLE_ROW]).required(),
  tableId: yup.string().required(),
  rowId: yup.string().required(),
  cells: rowCellsSelectionsSchema,
  waitForAsync: yup.boolean().notRequired(),
  waitForAsyncTimeout: yup.number().notRequired(),
})

export const deleteTableRowSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.DELETE_TABLE_ROW]).required(),
  tableId: yup.string().required(),
  rowId: yup.string().required(),
})

// Mode for bulk upsert operations
export enum BulkUpsertMode {
  UPSERT = 'UPSERT',
  INSERT_ONLY = 'INSERT_ONLY',
  UPDATE_ONLY = 'UPDATE_ONLY',
}

/**
 * Selections schema for BULK_UPSERT_TABLE_ROW action step.
 * Performs bulk upsert operations on table rows based on unique column(s).
 *
 * The action step will:
 * 1. Query existing rows using the unique column(s)
 * 2. Partition rows into creates vs updates based on matches
 * 3. Perform bulk insert for new rows
 * 4. Perform bulk update for existing rows
 * 5. Trigger side effects (formulas, post-row jobs)
 */
export const bulkUpsertTableRowSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.BULK_UPSERT_TABLE_ROW]).required(),
  /** ID of the table to upsert rows into */
  tableId: yup.string().required(),
  /** Optional: ID of the parent row to reference. If provided, the rows will be upserted as children of the parent row. */
  parentRowId: yup.string().notRequired(),
  /** Column IDs that uniquely identify rows (can be single or composite key) */
  uniqueColumnIds: yup.array().of(yup.string().required()).min(1).required(),
  /** Rows to upsert */
  rows: yup
    .mixed<MappedArray<RowCellsSelections> | RowCellsSelections[]>(
      (value): value is MappedArray<RowCellsSelections> | RowCellsSelections[] => {
        return (
          mappedRowsSelectionsSchema.isValidSync(value) ||
          multipleRowsCellsSelectionsSchema.isValidSync(value)
        )
      },
    )
    .required(),
  /** Optional: If provided, only update these columns on conflict. If not provided, update all columns. */
  onConflictUpdateColumnIds: yup.array().of(yup.string().required()).notRequired(),
  /** Mode for upsert operation: UPSERT (default), INSERT_ONLY, or UPDATE_ONLY */
  mode: yup.string().oneOf(Object.values(BulkUpsertMode)).notRequired(),
  waitForAsync: yup.boolean().notRequired(),
  waitForAsyncTimeout: yup.number().notRequired(),
})

export const tableQueryFilterSchema = yup
  .object({
    columnId: yup.string().required(),
    condition: yup.string().oneOf(Object.values(Condition)).required(),
    value: yup.lazy((_, { parent }) => {
      if (parent && isUnaryCondition(parent.condition)) {
        // For unary conditions, value is optional
        return actionDataSchema.nullable().optional()
      }
      // For non-unary conditions, value is required
      return actionDataSchema.nullable().defined()
    }),
  })
  .transform((value) => {
    // Strip __type discriminator field added by RJSF for oneOf resolution
    if (value && typeof value === 'object' && '__type' in value) {
      const { __type: _, ...rest } = value as Record<string, unknown>
      return rest
    }
    return value
  })
  .test(
    'value-required-for-non-unary',
    'value must be defined for non-unary conditions',
    function (obj) {
      // If the condition is unary (like IS_EMPTY, IS_NOT_EMPTY), value is not required
      if (obj && isUnaryCondition(obj.condition)) {
        return true
      }
      // For non-unary conditions, value is required
      return obj && obj.value !== undefined
    },
  )
  .strict()

/**
 * The filter checks that the data in the cell for the given `columnId` satisfies the `condition`
 * along with the given `value. For example, if the `columnId` is for "Status", the value is
 * "Pending" and the condition is "EQUAL" then we are looking for rows where the "Status" column has
 * the value "Pending".
 */
export type TableQueryFilter = yup.InferType<typeof tableQueryFilterSchema>

export type FilterExpression = {
  operator: BooleanOperator
  expressions: TableQueryFilterExpression[]
}

export type TableQueryFilterExpression = FilterExpression | TableQueryFilter

/**
 * Extracts all leaf filters from a polymorphic filters field
 * Handles both legacy array format and new nested expression format
 * Used for validation and processing where individual filters need to be examined
 *
 * This preserves the nested structure for execution while allowing access to individual filters
 */
export function extractTableQueryFilters(
  filters: TableQueryFilterExpression[] | undefined,
): TableQueryFilter[] {
  if (!filters) {
    return []
  }

  // Helper function to recursively extract leaf filters
  const extractLeafFilters = (expr: TableQueryFilterExpression): TableQueryFilter[] => {
    // If it's a leaf filter, return it
    if ('columnId' in expr) {
      return [expr]
    }

    // If it's an and/or expression, recursively extract from all sub-expressions
    if ('operator' in expr && 'expressions' in expr) {
      return expr.expressions.flatMap((subExpr) => extractLeafFilters(subExpr))
    }

    return []
  }

  // Process each item in the array - could be individual filters or nested expressions
  const result: TableQueryFilter[] = []

  for (const item of filters) {
    result.push(...extractLeafFilters(item))
  }

  return result
}

export const tableQueryFilterExpressionSchema: yup.Lazy<TableQueryFilterExpression> = yup.lazy(
  (value): yup.Schema<TableQueryFilterExpression> => {
    if (value && typeof value === 'object' && 'operator' in value) {
      return yup
        .object({
          operator: yup.string().oneOf(Object.values(BooleanOperator)).required(),
          expressions: yup.array().of(tableQueryFilterExpressionSchema).required(),
        })
        .transform((value) => {
          // Strip __type discriminator field added by RJSF for oneOf resolution
          if (value && typeof value === 'object' && '__type' in value) {
            const { __type: _, ...rest } = value as Record<string, unknown>
            return rest
          }
          return value
        })
        .strict() as yup.ObjectSchema<FilterExpression>
    }
    return tableQueryFilterSchema
  },
)

export const tableQuerySortSchema = yup
  .object({
    columnId: yup.string().required(),
    direction: yup.string().oneOf(Object.values(SortDirection)).required(),
  })
  .strict()

/**
 * The provided sort order for the table query. The `columnId` is the ID of the column to sort by
 * and the `direction` is the direction to sort in.
 */
export type TableQuerySort = yup.InferType<typeof tableQuerySortSchema>

export const tableQuerySelectionsSchema = yup.object({
  type: yup
    .string()
    .oneOf([ActionStepType.TABLE_QUERY] as const)
    .required(),
  tableId: yup.string().required(),
  maxRows: yup
    .mixed()
    .optional()
    .test(
      'is-string-or-number',
      'Value must be a string or number',
      (value) =>
        value === undefined || // allow it to be missing
        typeof value === 'string' ||
        typeof value === 'number',
    ),
  parentRowId: yup.string(),
  rowIds: yup.mixed<MappedStringArray | string[]>(
    (value): value is MappedStringArray | string[] =>
      getMappedStringArraySchema().isValidSync(value) || yup.array(yup.string()).isValidSync(value),
  ),
  // Array of TableQueryFilterExpression - supports both legacy and advanced formats
  // Processing will detect format based on presence of 'type' fields
  filters: yup.array().of(tableQueryFilterExpressionSchema).optional(),
  // Only used when filters contains flat TableQueryFilter array
  filterBooleanOperator: yup
    .mixed<BooleanOperator>()
    .oneOf(Object.values(BooleanOperator))
    .optional(),
  sorts: yup.array().of(tableQuerySortSchema.required()),
  useElasticsearch: yup.boolean().notRequired(),
  // Optional flag to include the parent row data on each child row
  // (parent.id matches row.parentRowId).
  includeParent: yup.boolean().notRequired(),
})

export const sendEmailSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.SEND_EMAIL]).required(),
  to: yup
    .mixed<string | string[] | MappedStringArray>(
      (value): value is string | string[] | MappedStringArray => {
        return (
          yup.string().required().isValidSync(value) ||
          yup.array().of(yup.string().required()).isValidSync(value) ||
          getMappedStringArraySchema().isValidSync(value)
        )
      },
    )
    .required(),
  from: yup.string().nullable(),
  senderName: yup.string().nullable(),
  cc: yup
    .mixed<string | string[] | MappedStringArray>(
      (value): value is string | string[] | MappedStringArray => {
        return (
          yup.string().isValidSync(value) ||
          yup.array().of(yup.string()).isValidSync(value) ||
          getMappedStringArraySchema().isValidSync(value)
        )
      },
    )
    .nullable()
    .optional(),
  bcc: yup
    .mixed<string | string[] | MappedStringArray>(
      (value): value is string | string[] | MappedStringArray => {
        return (
          yup.string().isValidSync(value) ||
          yup.array().of(yup.string()).isValidSync(value) ||
          getMappedStringArraySchema().isValidSync(value)
        )
      },
    )
    .nullable()
    .optional(),
  subject: yup.string().required(),
  message: yup.string().required(),
  attachmentIds: yup.mixed<MappedStringArray | string[]>(
    (value): value is MappedStringArray | string[] =>
      getMappedStringArraySchema().isValidSync(value) || yup.array(yup.string()).isValidSync(value),
  ),
  isHtml: yup.boolean().default(false),
  confirmSend: yup.boolean().notRequired(),
})

export enum LoopType {
  MAP = 'MAP',
  FOR = 'FOR',
}

export const mapSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.MAP]).required(),
  loopType: yup.mixed<LoopType>().oneOf([LoopType.MAP, LoopType.FOR]).notRequired().nonNullable(),
  valuesArray: yup.string().notRequired(),
  iterations: yup.number().notRequired(),
  isIterative: yup.boolean().notRequired(),
  chunkSize: yup.number().notRequired(),
})

export enum MergeType {
  ADD_FIELD = 'ADD_FIELD',
  ZIP = 'ZIP',
  CONCAT = 'CONCAT',
  CUSTOM = 'CUSTOM',
}

export const mergeSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.MERGE]).required(),
  mergeType: yup.string().oneOf(Object.values(MergeType)).required(),
  primaryArray: yup.string().required(),
  expression: logicExpressionSchema,
  secondaryDataFieldName: yup.string(),
  secondaryData: yup.string().required(),
})

export const demoSelectionsSchema = yup
  .object({
    type: yup.string().required(),
  })
  .strict(false)
  .noUnknown(false)

export const generateFileSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.GENERATE_FILE]).required(),
  fileType: yup.string().oneOf(Object.values(GeneratedFileType)).required(),
  fileName: yup.string().notRequired(),
  fileMappings: yup.lazy((obj) => {
    if (Array.isArray(obj)) {
      return yup
        .array()
        .of(yup.object().shape({ key: yup.string().required(), value: yup.string().required() }))
    }
    return yup.object(_.mapValues(obj, () => yup.mixed().nullable()))
  }),
})

export const mergeFilesSelectionsSchema = yup
  .object({
    type: yup.string().oneOf([ActionStepType.MERGE_FILES]).required(),
    documentIds: yup
      .array()
      .of(
        yup
          .mixed<string | string[] | MappedStringArray>(
            (value): value is string | string[] | MappedStringArray => {
              return (
                yup.string().required().isValidSync(value) ||
                yup.array().of(yup.string().required()).isValidSync(value) ||
                getMappedStringArraySchema().isValidSync(value)
              )
            },
          )
          .required(),
      )
      .required(),
    outputFileName: yup.string().optional(),
  })
  .strict()

export const writeIntegrationSelectionsSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionStepType.WRITE_INTEGRATION] as const)
      .required(),
    integrationType: yup.string().oneOf(WRITE_INTEGRATION_TYPES).required(),
    metadata: yup
      .object({
        mode: yup.string().oneOf(Object.values(WriteIntegrationMode)).required(),
        entity: yup.string().oneOf(Object.values(WriteIntegrationEntityType)).required(),
        data: yup.object().nullable(),
      })
      .required(),
    continueOnError: yup.boolean().optional(),
  })
  .strict()

export const readIntegrationSelectionsSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionStepType.READ_INTEGRATION] as const)
      .required(),
    integrationType: yup.string().oneOf(READ_INTEGRATION_TYPES).required(),
    metadata: yup
      .object({
        entity: yup.string().oneOf(Object.values(ReadIntegrationEntityType)).required(),
        data: yup.object().nullable(),
      })
      .required(),
    continueOnError: yup.boolean().optional(),
  })
  .strict()

export const parseFileSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.PARSE_FILE]).required(),
  documentId: yup.string().required(),
  fileType: yup.string().oneOf(Object.values(ParseFileType)).required(),
  options: yup
    .object({
      modelId: yup.string().notRequired().meta({ globalEntityReference: true }),
    })
    .noUnknown(false)
    .required(),
})

export const branchSelectionsSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionStepType.BRANCH] as const)
      .required(),
    branches: yup
      .array()
      .of(
        yup.object({
          order: yup.number(),
          expression: logicExpressionSchema.required(),
          actionStepGroupId: yup.string().required(),
        }),
      )
      .required(),
  })
  .strict()

export const uniqueBySelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.UNIQUE_BY]).required(),
  arrayPath: yup.string().required(),
  uniqueKey: yup.string().required(),
})

export const groupBySelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.GROUP_BY]).required(),
  arrayPath: yup.string().required(),
  groupByKey: yup.string().required(),
})

export const groupByMultipleSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.GROUP_BY_MULTIPLE]).required(),
  arrayPath: yup.string().required(),
  groupByKeys: yup.array().of(yup.string().required()).min(1).required(),
  // Optional display name for each groupByKey; when set, used as the property name in the composite key output (matched by index). When omitted, falls back to path-based names for backwards compatibility.
  groupByKeyNames: yup
    .array()
    .of(yup.string().trim().required())
    .optional()
    .nullable()
    .test(
      'same-length-when-present',
      'Key names must have the same length as group by keys when provided',
      function (keyNames) {
        if (!Array.isArray(keyNames) || keyNames.length === 0) {
          return true
        }
        const keys = this.parent?.groupByKeys
        return Array.isArray(keys) && keys.length === keyNames.length
      },
    ),
})

export const uniqueByMultipleSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.UNIQUE_BY_MULTIPLE]).required(),
  arrayPath: yup.string().required(),
  uniqueKeys: yup.array().of(yup.string().required()).min(1).required(),
})

export const arraySelectSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.ARRAY_SELECT]).required(),
  arrayPath: yup.string().required(),
  key: yup.string().required(),
})

export const datastoreWriteSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.DATASTORE_WRITE]).required(),
  datastoreKey: yup.string().required(),
  operation: yup.string().oneOf(ALL_DATASTORE_MUTATION_OPERATIONS).required(),
  value: yup.string().when('operation', {
    is: DatastoreArrayMutationOperation.CLEAR,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.required(),
  }),
})

export const apiResponseSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.API_RESPONSE]).required(),
  data: yup
    .object({
      body: yup.string().required(),
    })
    .required(),
})

export const whileSelectionsSchema = yup
  .object({
    type: yup.string().oneOf([ActionStepType.WHILE]).required(),
    expression: logicExpressionSchema.required(),
    maxIterations: yup.number().max(100000).min(1).required(),
  })
  .strict()

export const notificationSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.NOTIFICATION]).required(),
  notificationType: yup.string().oneOf(Object.values(ACTION_STEP_NOTIFICATION_TYPES)).required(),
  notificationTargetType: yup.string().oneOf(Object.values(NOTIFICATION_TARGET_TYPES)).required(),
  userSelectMethod: yup
    .string()
    .oneOf(Object.values(NOTIFICATION_USER_SELECT_METHODS))
    .notRequired(),
  userId: yup.string().when('notificationTargetType', {
    is: (notificationTargetType: string) =>
      notificationTargetType === NOTIFICATION_TARGET_TYPES.SINGLE_USER,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  roleId: yup.string().when('notificationTargetType', {
    is: (notificationTargetType: string) =>
      notificationTargetType === NOTIFICATION_TARGET_TYPES.ROLES,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  title: yup.string().required(),
  message: yup.string().required(),
  linkToType: yup.string().oneOf(Object.values(LINK_TO_TYPES)).required(),
  linkToObjectId: yup.string().when('linkToType', {
    is: (linkToType: string) => linkToType !== LINK_TO_TYPES.NONE,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export const aiPromptSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.AI_PROMPT]).required(),
  contextMap: yup.string().oneOf(Object.values(AI_PROMPT_CONTEXT_TYPES)).required(),
  specificContextObject: yup
    .array()
    .of(
      yup.object({
        key: yup.string().notRequired(),
        value: yup.mixed().notRequired(),
      }),
    )
    .when('contextMap', {
      is: (contextMap: string) =>
        contextMap === AI_PROMPT_CONTEXT_TYPES.SPECIFIC_ACTION_STEP_OUTPUTS,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  prompt: yup.string().required(),
  modelId: yup.string().required().meta({ globalEntityReference: true }),
})

export const generateBarcodeSelectionsSchema = yup.object({
  type: yup.string().oneOf([ActionStepType.GENERATE_BARCODE]).required(),
  barcodeType: yup.string().oneOf(Object.values(BARCODE_TYPES)).required(),
  textInput: yup.string().required(),
})

/**
 * Maximum delay time in seconds (1 day = 86400 seconds)
 */
export const MAX_DELAY_ACTION_STEP_SECONDS = 1 * 60 * 60 * 24

export const delaySelectionsSchema = yup
  .object({
    type: yup.string().oneOf([ActionStepType.DELAY]).required(),
    delayTimeInSeconds: yup.number().required().min(0).max(MAX_DELAY_ACTION_STEP_SECONDS),
  })
  .strict()

// Inferred types

export type FormulaSelections = yup.InferType<typeof formulaSelectionsSchema>

export type InsertTableRowSelections = yup.InferType<typeof insertTableRowSelectionsSchema>

export type UpdateTableRowSelections = yup.InferType<typeof updateTableRowSelectionsSchema>

export type DeleteTableRowSelections = yup.InferType<typeof deleteTableRowSelectionsSchema>

export type BulkUpsertTableRowSelections = yup.InferType<typeof bulkUpsertTableRowSelectionsSchema>

export type TableQuerySelections = yup.InferType<typeof tableQuerySelectionsSchema>

export type SendEmailSelections = yup.InferType<typeof sendEmailSelectionsSchema>

export type MergeSelections = yup.InferType<typeof mergeSelectionsSchema>

export type MapSelections = yup.InferType<typeof mapSelectionsSchema>

export type WhileSelections = yup.InferType<typeof whileSelectionsSchema>

export type DemoSelections = yup.InferType<typeof demoSelectionsSchema> & {
  [key: string]: JsonValue | undefined
}

export type GenerateFileSelections = yup.InferType<typeof generateFileSelectionsSchema>

export type MergeFilesSelections = yup.InferType<typeof mergeFilesSelectionsSchema>

export type WriteIntegrationSelections = yup.InferType<typeof writeIntegrationSelectionsSchema>

export type ReadIntegrationSelections = yup.InferType<typeof readIntegrationSelectionsSchema>

export type ParseFileSelections = yup.InferType<typeof parseFileSelectionsSchema>

export type BranchSelections = yup.InferType<typeof branchSelectionsSchema>

export type UniqueBySelections = yup.InferType<typeof uniqueBySelectionsSchema>

export type GroupBySelections = yup.InferType<typeof groupBySelectionsSchema>

export type GroupByMultipleSelections = yup.InferType<typeof groupByMultipleSelectionsSchema>

export type UniqueByMultipleSelections = yup.InferType<typeof uniqueByMultipleSelectionsSchema>

export type ArraySelectSelections = yup.InferType<typeof arraySelectSelectionsSchema>

export type DatastoreWriteSelections = yup.InferType<typeof datastoreWriteSelectionsSchema>

export type ApiResponseSelections = yup.InferType<typeof apiResponseSelectionsSchema>

export type NotificationSelections = yup.InferType<typeof notificationSelectionsSchema>

export type AIPromptSelections = yup.InferType<typeof aiPromptSelectionsSchema>

export type GenerateBarcodeSelections = yup.InferType<typeof generateBarcodeSelectionsSchema>

export type DelaySelections = yup.InferType<typeof delaySelectionsSchema>

/**
 * Action step selections can either be fully resolved `ActionStepMetadata` objects, or they can be
 * valid selections that need to be resolved by the action step processor.
 */
export type ActionStepSelections =
  | BranchSelections
  | FormulaSelections
  | InsertTableRowSelections
  | UpdateTableRowSelections
  | DeleteTableRowSelections
  | BulkUpsertTableRowSelections
  | TableQuerySelections
  | SendEmailSelections
  | MergeSelections
  | MapSelections
  | DemoSelections
  | GenerateFileSelections
  | MergeFilesSelections
  | WriteIntegrationSelections
  | ReadIntegrationSelections
  | ParseFileSelections
  | UniqueBySelections
  | GroupBySelections
  | GroupByMultipleSelections
  | UniqueByMultipleSelections
  | ArraySelectSelections
  | ApiResponseSelections
  | WhileSelections
  | NotificationSelections
  | AIPromptSelections
  | GenerateBarcodeSelections
  | DelaySelections

/**
 * Returns boolean on whether a given object is a valid `ActionStepSelections` object
 */
export function isActionStepSelections(
  input: Record<string, unknown>,
  type: ActionStepType,
): input is ActionStepSelections {
  switch (type) {
    case ActionStepType.FORMULA:
      return formulaSelectionsSchema.isValidSync(input)
    case ActionStepType.DATASTORE_WRITE:
      return datastoreWriteSelectionsSchema.isValidSync(input)
    case ActionStepType.DEMO:
      return demoSelectionsSchema.isValidSync(input)
    case ActionStepType.INSERT_TABLE_ROW:
      return insertTableRowSelectionsSchema.isValidSync(input)
    case ActionStepType.UPDATE_TABLE_ROW:
      return updateTableRowSelectionsSchema.isValidSync(input)
    case ActionStepType.DELETE_TABLE_ROW:
      return deleteTableRowSelectionsSchema.isValidSync(input)
    case ActionStepType.BULK_UPSERT_TABLE_ROW:
      return bulkUpsertTableRowSelectionsSchema.isValidSync(input)
    case ActionStepType.TABLE_QUERY:
      return tableQuerySelectionsSchema.isValidSync(input)
    case ActionStepType.BRANCH:
      return branchSelectionsSchema.isValidSync(input)
    case ActionStepType.SEND_EMAIL:
      return sendEmailSelectionsSchema.isValidSync(input)
    case ActionStepType.READ_INTEGRATION:
      return readIntegrationSelectionsSchema.isValidSync(input)
    case ActionStepType.WRITE_INTEGRATION:
      return writeIntegrationSelectionsSchema.isValidSync(input)
    case ActionStepType.MAP:
      return mapSelectionsSchema.isValidSync(input)
    case ActionStepType.MERGE:
      return mergeSelectionsSchema.isValidSync(input)
    case ActionStepType.GENERATE_FILE:
      return generateFileSelectionsSchema.isValidSync(input)
    case ActionStepType.MERGE_FILES:
      return mergeFilesSelectionsSchema.isValidSync(input)
    case ActionStepType.PARSE_FILE:
      return parseFileSelectionsSchema.isValidSync(input)
    case ActionStepType.UNIQUE_BY:
      return uniqueBySelectionsSchema.isValidSync(input)
    case ActionStepType.GROUP_BY:
      return groupBySelectionsSchema.isValidSync(input)
    case ActionStepType.GROUP_BY_MULTIPLE:
      return groupByMultipleSelectionsSchema.isValidSync(input)
    case ActionStepType.UNIQUE_BY_MULTIPLE:
      return uniqueByMultipleSelectionsSchema.isValidSync(input)
    case ActionStepType.ARRAY_SELECT:
      return arraySelectSelectionsSchema.isValidSync(input)
    case ActionStepType.API_RESPONSE:
      return apiResponseSelectionsSchema.isValidSync(input)
    case ActionStepType.WHILE:
      return whileSelectionsSchema.isValidSync(input)
    case ActionStepType.NOTIFICATION:
      return notificationSelectionsSchema.isValidSync(input)
    case ActionStepType.AI_PROMPT:
      return aiPromptSelectionsSchema.isValidSync(input)
    case ActionStepType.GENERATE_BARCODE:
      return generateBarcodeSelectionsSchema.isValidSync(input)
    case ActionStepType.DELAY:
      return delaySelectionsSchema.isValidSync(input)
  }
}

/**
 * Checks if the given object is a valid `ActionStepSelections` object. If not, throws an error.
 */
export function validateActionStepSelections(
  input: Record<string, unknown>,
  type: ActionStepType,
): ActionStepSelections {
  switch (type) {
    case ActionStepType.AI_PROMPT: {
      return aiPromptSelectionsSchema.validateSync(input)
    }
    case ActionStepType.FORMULA: {
      return formulaSelectionsSchema.validateSync(input)
    }
    case ActionStepType.DATASTORE_WRITE: {
      return datastoreWriteSelectionsSchema.validateSync(input)
    }
    case ActionStepType.DEMO: {
      return demoSelectionsSchema.validateSync(input)
    }
    case ActionStepType.INSERT_TABLE_ROW: {
      return insertTableRowSelectionsSchema.validateSync(input)
    }
    case ActionStepType.UPDATE_TABLE_ROW: {
      return updateTableRowSelectionsSchema.validateSync(input)
    }
    case ActionStepType.DELETE_TABLE_ROW: {
      return deleteTableRowSelectionsSchema.validateSync(input)
    }
    case ActionStepType.BULK_UPSERT_TABLE_ROW: {
      return bulkUpsertTableRowSelectionsSchema.validateSync(input)
    }
    case ActionStepType.TABLE_QUERY: {
      return tableQuerySelectionsSchema.validateSync(input)
    }
    case ActionStepType.BRANCH: {
      return branchSelectionsSchema.validateSync(input)
    }
    case ActionStepType.SEND_EMAIL: {
      return sendEmailSelectionsSchema.validateSync(input)
    }
    case ActionStepType.READ_INTEGRATION: {
      return readIntegrationSelectionsSchema.validateSync(input)
    }
    case ActionStepType.WRITE_INTEGRATION: {
      return writeIntegrationSelectionsSchema.validateSync(input)
    }
    case ActionStepType.MAP: {
      return mapSelectionsSchema.validateSync(input)
    }
    case ActionStepType.MERGE: {
      return mergeSelectionsSchema.validateSync(input)
    }
    case ActionStepType.GENERATE_FILE: {
      return generateFileSelectionsSchema.validateSync(input)
    }
    case ActionStepType.MERGE_FILES: {
      return mergeFilesSelectionsSchema.validateSync(input)
    }
    case ActionStepType.PARSE_FILE: {
      return parseFileSelectionsSchema.validateSync(input)
    }
    case ActionStepType.UNIQUE_BY: {
      return uniqueBySelectionsSchema.validateSync(input)
    }
    case ActionStepType.GROUP_BY: {
      return groupBySelectionsSchema.validateSync(input)
    }
    case ActionStepType.GROUP_BY_MULTIPLE: {
      return groupByMultipleSelectionsSchema.validateSync(input)
    }
    case ActionStepType.UNIQUE_BY_MULTIPLE: {
      return uniqueByMultipleSelectionsSchema.validateSync(input)
    }
    case ActionStepType.ARRAY_SELECT: {
      return arraySelectSelectionsSchema.validateSync(input)
    }
    case ActionStepType.API_RESPONSE: {
      return apiResponseSelectionsSchema.validateSync(input)
    }
    case ActionStepType.WHILE: {
      return whileSelectionsSchema.validateSync(input)
    }
    case ActionStepType.NOTIFICATION: {
      return notificationSelectionsSchema.validateSync(input)
    }
    case ActionStepType.GENERATE_BARCODE: {
      return generateBarcodeSelectionsSchema.validateSync(input)
    }
    case ActionStepType.DELAY: {
      return delaySelectionsSchema.validateSync(input)
    }
  }
}

const selectionsSchemasByType: Partial<Record<ActionStepType, yup.ObjectSchema<object>>> = {
  [ActionStepType.AI_PROMPT]: aiPromptSelectionsSchema,
  [ActionStepType.FORMULA]: formulaSelectionsSchema,
  [ActionStepType.DATASTORE_WRITE]: datastoreWriteSelectionsSchema,
  [ActionStepType.DEMO]: demoSelectionsSchema,
  [ActionStepType.INSERT_TABLE_ROW]: insertTableRowSelectionsSchema,
  [ActionStepType.UPDATE_TABLE_ROW]: updateTableRowSelectionsSchema,
  [ActionStepType.DELETE_TABLE_ROW]: deleteTableRowSelectionsSchema,
  [ActionStepType.BULK_UPSERT_TABLE_ROW]: bulkUpsertTableRowSelectionsSchema,
  [ActionStepType.TABLE_QUERY]: tableQuerySelectionsSchema,
  [ActionStepType.BRANCH]: branchSelectionsSchema,
  [ActionStepType.SEND_EMAIL]: sendEmailSelectionsSchema,
  [ActionStepType.READ_INTEGRATION]: readIntegrationSelectionsSchema,
  [ActionStepType.WRITE_INTEGRATION]: writeIntegrationSelectionsSchema,
  [ActionStepType.MAP]: mapSelectionsSchema,
  [ActionStepType.MERGE]: mergeSelectionsSchema,
  [ActionStepType.GENERATE_FILE]: generateFileSelectionsSchema,
  [ActionStepType.MERGE_FILES]: mergeFilesSelectionsSchema,
  [ActionStepType.PARSE_FILE]: parseFileSelectionsSchema,
  [ActionStepType.UNIQUE_BY]: uniqueBySelectionsSchema,
  [ActionStepType.GROUP_BY]: groupBySelectionsSchema,
  [ActionStepType.GROUP_BY_MULTIPLE]: groupByMultipleSelectionsSchema,
  [ActionStepType.UNIQUE_BY_MULTIPLE]: uniqueByMultipleSelectionsSchema,
  [ActionStepType.ARRAY_SELECT]: arraySelectSelectionsSchema,
  [ActionStepType.API_RESPONSE]: apiResponseSelectionsSchema,
  [ActionStepType.WHILE]: whileSelectionsSchema,
  [ActionStepType.NOTIFICATION]: notificationSelectionsSchema,
  [ActionStepType.GENERATE_BARCODE]: generateBarcodeSelectionsSchema,
  [ActionStepType.DELAY]: delaySelectionsSchema,
}

/**
 * A tree structure encoding which field paths contain global entity references.
 * `true` at a leaf means that field's value should not be UUID-remapped.
 * A nested object means descend further to find annotated fields.
 *
 * Example: AI_PROMPT → `{ modelId: true }`
 *          PARSE_FILE → `{ options: { modelId: true } }`
 */
export type GlobalEntityFieldTree = { [key: string]: true | GlobalEntityFieldTree }

type SchemaDescription = {
  fields?: Record<string, SchemaDescription>
  meta?: Record<string, unknown>
}

function buildGlobalEntityFieldTree(description: SchemaDescription): GlobalEntityFieldTree | null {
  if (!description.fields) {
    return null
  }
  const tree: GlobalEntityFieldTree = {}
  let hasEntries = false
  for (const [key, field] of Object.entries(description.fields)) {
    if ((field as SchemaDescription).meta?.globalEntityReference) {
      tree[key] = true
      hasEntries = true
    } else {
      const subtree = buildGlobalEntityFieldTree(field as SchemaDescription)
      if (subtree) {
        tree[key] = subtree
        hasEntries = true
      }
    }
  }
  return hasEntries ? tree : null
}

/**
 * Returns a tree encoding which field paths in the Yup selections schema for the given
 * ActionStepType are annotated with `.meta({ globalEntityReference: true })`.
 * These fields reference global (non-org-scoped) entities whose UUIDs must not be
 * remapped during org duplication.
 */
export const getGlobalEntityFieldTree = _.memoize(
  (type: ActionStepType): GlobalEntityFieldTree | null => {
    const schema = selectionsSchemasByType[type]
    if (!schema) {
      return null
    }
    return buildGlobalEntityFieldTree(schema.describe() as SchemaDescription)
  },
)

/**
 * Type guard to check if a value is an array operation wrapper object and the operation is
 * append, replace, or remove. This is so to not confuse it with the map/loop array operations.
 */
function isArrayOperationWrapper(
  value: unknown,
): value is { operation?: string | null; value?: unknown } {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'operation' in value &&
    (value.operation === 'append' || value.operation === 'replace' || value.operation === 'remove')
  )
}

/**
 * Helper function to clean up incomplete array operation wrappers
 *
 * Background: When users interact with the array operation UI, they first select an operation
 * from a dropdown (append/replace/remove), then provide values. If they select an operation
 * but don't provide values before saving, we end up with incomplete data like:
 * { operation: 'append', value: null }
 *
 * This function removes such incomplete operations from the selections data to prevent
 * them from being saved to the database.
 *
 * Why all operations require values:
 * - 'append': Must specify which items to append to the array
 * - 'remove': Must specify which items to remove from the array
 * - 'replace': Must specify what the new array contents should be
 *
 * Note: If operation is null/undefined but value exists, that's valid - it means
 * "use default behavior (replace)" with the provided value.
 */
export function cleanupIncompleteArrayOperations(
  selectionsData: Record<string, unknown>,
  stepType: ActionStepType,
): Record<string, unknown> {
  // Only clean up UPDATE_TABLE_ROW steps (CREATE_TABLE_ROW doesn't have array operations)
  if (stepType !== ActionStepType.UPDATE_TABLE_ROW) {
    return selectionsData
  }

  // Don't validate schema - schema allows incomplete operations (operation set, value null)
  // We're cleaning them up here as a business logic decision, not a validation rule
  const cells = selectionsData.cells

  // If no cells, nothing to clean
  if (cells === null || cells === undefined) {
    return selectionsData
  }

  // Ensure cells is an object we can iterate over
  if (typeof cells !== 'object' || Array.isArray(cells)) {
    return selectionsData
  }

  // Build a new cells object, filtering out incomplete array operations
  const cleanedCells: Record<string, unknown> = {}

  for (const [columnId, cellValue] of Object.entries(cells)) {
    const isWrapper = isArrayOperationWrapper(cellValue)

    if (isWrapper) {
      // If operation is explicitly set (not null/undefined) but value is missing,
      // this is incomplete and should be removed
      const hasExplicitOperation = cellValue.operation !== null && cellValue.operation !== undefined
      const hasMissingValue = cellValue.value === undefined || cellValue.value === null

      const shouldRemoveIncompleteOperation = hasExplicitOperation && hasMissingValue

      // Only keep this cell if it's complete
      if (!shouldRemoveIncompleteOperation) {
        cleanedCells[columnId] = cellValue
      }
    } else {
      // Keep non-wrapper cells as-is
      cleanedCells[columnId] = cellValue
    }
  }

  return {
    ...selectionsData,
    cells: cleanedCells,
  }
}
